import { supabase } from './supabase';
// The app's i18next singleton (client/src/i18n/index.ts). Safe to import here:
// that module depends only on i18next packages, so no circular import.
import i18n from '../i18n';

const BASE = '/api/trpc';

// Read the stored Supabase session straight from localStorage. Returns the
// access token AND its expiry (unix seconds) so callers can tell a FRESH token
// from an EXPIRED one. Supabase access tokens live ~1h; the value blob is the
// session object { access_token, expires_at, refresh_token, ... } (older shapes
// nested it under currentSession).
function getStoredToken(): { token: string; expiresAt: number | null } | null {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) {
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        const parsed = JSON.parse(raw);
        const sess = parsed?.currentSession ?? parsed;
        const token = sess?.access_token;
        if (token) {
          const expiresAt = typeof sess?.expires_at === 'number' ? sess.expires_at : null;
          return { token, expiresAt };
        }
      }
    }
  } catch (e) {
    console.error('[trpc] localStorage token read failed:', e);
  }
  return null;
}

async function authHeaders(): Promise<Record<string, string>> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };

  const stored = getStoredToken();
  const nowSec = Date.now() / 1000;

  // FAST PATH: a cached token that is NOT expired (60s safety margin). Sending
  // an expired token is what made accounts "stop responding" after ~1h: every
  // protected call 401'd ("غير مصرح") because the cached token was used blindly
  // without a refresh.
  if (stored?.token && stored.expiresAt && stored.expiresAt - 60 > nowSec) {
    h['Authorization'] = `Bearer ${stored.token}`;
    return h;
  }

  // REFRESH PATH: token missing or expired/near-expiry → getSession() refreshes
  // it (and rewrites localStorage) when needed. Timeout-guarded so a stuck
  // navigator.locks refresh can't hang the request forever.
  try {
    const sessionPromise = supabase.auth.getSession();
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('getSession timeout after 5s')), 5000)
    );
    const { data } = await Promise.race([sessionPromise, timeoutPromise]) as any;
    const token = data?.session?.access_token;
    if (token) h['Authorization'] = `Bearer ${token}`;
  } catch (e) {
    console.error('[trpc] authHeaders refresh error:', e);
  }

  // Last resort: if the refresh could not produce a token (e.g. a transient
  // lock timeout) but we still hold a cached one, send it rather than nothing.
  if (!h['Authorization'] && stored?.token) {
    h['Authorization'] = `Bearer ${stored.token}`;
  }
  return h;
}

// Translate a server error messageKey (the server's errorFormatter in
// server/_core/trpc-init.ts puts bilingual keys like
// 'errors.validation.generic' in error.data.messageKey). Keys in the errors.*
// namespace resolve to { title, body, cta } objects, so we read the
// title/body leaves first, then fall back to a plain-string key.
// Returns null when nothing translatable exists (e.g. i18n not loaded yet).
function translateErrorKey(key: string, params?: Record<string, unknown>): string | null {
  try {
    const opts = { ...(params ?? {}), defaultValue: '' };
    const title = String(i18n.t(`${key}.title`, opts) || '');
    const body = String(i18n.t(`${key}.body`, opts) || '');
    if (title && body) return `${title}. ${body}`;
    if (body || title) return body || title;
    const flat = i18n.t(key, opts);
    if (typeof flat === 'string' && flat && !flat.startsWith('key ')) return flat;
  } catch {
    /* i18n not ready - the guard in handle() still protects the UI */
  }
  return null;
}

function handle(j: any) {
  if (j?.error) {
    const code = j.error.data?.code;
    if (code === 'UNAUTHORIZED') {
      throw new Error('غير مصرّح - يرجى تسجيل الدخول');
    }
    const messageKey: string | undefined = j.error.data?.messageKey;
    const params: Record<string, unknown> | undefined =
      j.error.data?.params ?? j.error.data?.messageParams;
    let message: string = j.error.message || 'Request failed';
    if (messageKey) {
      const translated = translateErrorKey(messageKey, params);
      if (translated) message = translated;
    }
    // Defensive guard: a raw Zod issues dump (or any JSON blob) must never
    // reach the UI. If the message still looks like JSON, swap in the
    // generic validation message.
    if (/^\s*[\[{]/.test(message)) {
      message =
        translateErrorKey('errors.validation.generic', params) ||
        ((i18n.language || 'ar').startsWith('ar')
          ? 'البيانات المدخلة غير صالحة، راجع الحقول وحاول مرة أخرى'
          : 'The submitted data is not valid. Review the fields and try again');
    }
    const err = new Error(message) as Error & { messageKey?: string };
    if (messageKey) err.messageKey = messageKey;
    throw err;
  }
  return j?.result?.data ?? j?.result ?? j;
}

export async function trpcQuery<T = any>(name: string, input?: any): Promise<T> {
  const url = input !== undefined
    ? `${BASE}/${name}?input=${encodeURIComponent(JSON.stringify(input))}`
    : `${BASE}/${name}`;
  const res = await fetch(url, { headers: await authHeaders() });
  const j = await res.json();
  return handle(j);
}

export async function trpcMutation<T = any>(name: string, input: any = {}): Promise<T> {
  console.log('[trpc] trpcMutation called for:', name);
  console.log('[trpc] Getting auth headers...');
  const headers = await authHeaders();
  console.log('[trpc] Got headers, hasAuth:', !!headers.Authorization);
  console.log('[trpc] Sending fetch to:', `${BASE}/${name}`);
  const res = await fetch(`${BASE}/${name}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(input),
  });
  console.log('[trpc] Fetch returned, status:', res.status);
  const j = await res.json();
  console.log('[trpc] Parsed JSON response');
  const result = handle(j);
  // Best-effort activity log emission. Never throws; never blocks.
  fireActivityForMutation(name, input).catch(() => {});
  return result;
}

// ─────────────────────────────────────────────
// Activity interceptor — emits a dashboard.logActivity call after a
// successful mutation on radar/resume/content/profile endpoints. Silent
// failures by design — analytics must never break the user's flow.
// ─────────────────────────────────────────────

type ActivityMapEntry = {
  action: string;
  pillar: 'radar' | 'resume' | 'content' | 'profile' | 'wallet' | 'dashboard';
};

const ACTIVITY_MAP: Record<string, ActivityMapEntry> = {
  'radar.run':                    { action: 'radar.completed',              pillar: 'radar' },
  'radar.applyFix':               { action: 'radar.applied_fix',            pillar: 'radar' },
  'radar.revertFix':              { action: 'radar.reverted_fix',           pillar: 'radar' },
  'resume.build':                 { action: 'resume.built',                 pillar: 'resume' },
  'resume.createNewVersion':      { action: 'resume.new_version_built',     pillar: 'resume' },
  'resume.refine':                { action: 'resume.refined',               pillar: 'resume' },
  'resume.archive':               { action: 'resume.archived',              pillar: 'resume' },
  'resume.exportPdf':             { action: 'resume.exported_pdf',          pillar: 'resume' },
  'resume.exportDocx':            { action: 'resume.exported_docx',         pillar: 'resume' },
  'content.generatePost':         { action: 'content.post.created',         pillar: 'content' },
  'content.generateCarousel':     { action: 'content.carousel.created',     pillar: 'content' },
  'content.generateRepurpose':    { action: 'content.repurpose_bundle.created', pillar: 'content' },
  'content.refine':               { action: 'content.refined',              pillar: 'content' },
  'content.markPublished':        { action: 'content.published',            pillar: 'content' },
  'content.exportCarouselPdf':    { action: 'content.exported_pdf',         pillar: 'content' },
  'coverLetter.generate':          { action: 'cover_letter.generated',       pillar: 'resume' },
  'careerProfile.create':         { action: 'onboarding.completed',         pillar: 'profile' },
  'careerProfile.update':         { action: 'career_profile.updated',       pillar: 'profile' },
};

async function fireActivityForMutation(name: string, input: unknown): Promise<void> {
  const entry = ACTIVITY_MAP[name];
  if (!entry) return;
  // Avoid recursion / pointless self-logging
  if (name.startsWith('dashboard.')) return;

  const headers = await authHeaders();
  if (!headers.Authorization) return;

  const payload: Record<string, unknown> = { source: name };
  if (input && typeof input === 'object') {
    const inputObj = input as Record<string, unknown>;
    if (typeof inputObj.language === 'string') payload.language = inputObj.language;
    if (typeof inputObj.topic === 'string') payload.topic = inputObj.topic;
    if (typeof inputObj.targetRole === 'string') payload.target_role = inputObj.targetRole;
    if (typeof inputObj.overrideTargetRole === 'string') payload.target_role = inputObj.overrideTargetRole;
    if (typeof inputObj.templateId === 'string') payload.template_id = inputObj.templateId;
  }
  const language = (input && typeof input === 'object' && (input as Record<string, unknown>).language) as
    | 'ar'
    | 'en'
    | undefined;

  try {
    await fetch(`${BASE}/dashboard.logActivity`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        action: entry.action,
        pillar: entry.pillar,
        payload,
        language,
      }),
    });
  } catch {
    /* swallow */
  }
}

export const trpc = {
  token: {
    balance: () => trpcQuery<{ balance: number; plan?: string; serverTimestamp?: string; profileUpdatedAt?: string | null }>('token.balance'),
    getMyBalance: () => trpcQuery<{ balance: number; plan?: string; serverTimestamp?: string; profileUpdatedAt?: string | null }>('token.getMyBalance'),
    history: () => trpcQuery<any[]>('token.history'),
  },
  linkedin: {
    analyze: (profileUrl: string) =>
      trpcMutation<any>('linkedin.analyze', { profileUrl }),
    analyzeDeep: (input: { linkedinUrl?: string; imageBase64?: string; mediaType?: string }) =>
      trpcMutation<any>('linkedin.analyzeDeep', input),
    history: () => trpcQuery<any[]>('linkedin.history'),
  },
  cv: {
    parseUpload: (input: { fileBase64: string; fileName: string; mimeType: string }) =>
      trpcMutation<{ success: boolean; extracted: any; rawText: string; textLength: number; parseMethod: 'docx' | 'pdf-text' | 'pdf-ocr' | 'manual' }>('cv.parseUpload', input),
    generate: (input: {
      userData: any;
      targetRole: string;
      targetCompany?: string;
      jobDescription?: string;
      template: 'mit-classic' | 'harvard-executive';
      language: 'ar' | 'en';
      includeCoverLetter?: boolean;
      calculateATS?: boolean;
      sourceParseMethod?: 'docx' | 'pdf-text' | 'pdf-ocr' | 'manual';
    }) => trpcMutation<{
      id: string;
      cvData: any;
      docxUrl: string | null;
      pdfUrl: string | null;
      atsScore: any | null;
      coverLetter: { docxUrl: string | null; pdfUrl: string | null } | null;
      tokensUsed: number;
      tokensRemaining: number;
    }>('cv.generate', input),
    compareCvs: (input: { olderId: string; newerId: string }) =>
      trpcQuery<{
        older: { id: string; template: string; targetRole: string; createdAt: string; atsScore: number | null };
        newer: { id: string; template: string; targetRole: string; createdAt: string; atsScore: number | null };
        segments: Array<{ type: 'added' | 'removed' | 'unchanged'; text: string }>;
        metrics: { wordsAdded: number; wordsRemoved: number; atsDelta: number | null };
      }>('cv.compareCvs', input),
    list: () => trpcQuery<any[]>('cv.list'),
    listForResumeList: () => trpcQuery<{ cvs: Array<{ id: string; targetRole: string; template: string; language: string; atsScore: number | null; createdAt: string }> }>('cv.listForResumeList'),
    getById: (input: { id: string }) => trpcQuery<any>('cv.getById', input),
    deleteById: (input: { id: string }) => trpcMutation<{ success: boolean }>('cv.deleteById', input),
    saveUploaded: (input: {
      extracted: any;
      extractedText: string;
      fileName: string;
      parseMethod: 'docx' | 'pdf-text' | 'pdf-ocr' | 'manual';
      // V2 audit point 13: multi-document support.
      docType?: UploadedDocType;
      append?: boolean;
    }) => trpcMutation<{ success: boolean; fileName: string; uploadedAt: string; documentsCount: number }>('cv.saveUploaded', input),
    removeUploadedDoc: (input: { index: number }) =>
      trpcMutation<{ success: boolean; documentsCount: number }>('cv.removeUploadedDoc', input),
    getUploaded: () => trpcQuery<
      | { exists: false }
      | {
          exists: true;
          fileName: string;
          parseMethod: 'docx' | 'pdf-text' | 'pdf-ocr' | 'manual';
          uploadedAt: string;
          detected_language?: 'ar' | 'en' | null;
          documents?: Array<{ fileName: string; docType: UploadedDocType; addedAt: string }>;
        }
    >('cv.getUploaded'),
    getAutofill: () => trpcQuery<{
      source: 'cv' | 'radar' | 'none';
      fileName: string | null;
      data: Partial<CVManualFormData>;
    }>('cv.getAutofill'),
    // V2 audit point 16: per-field AI suggestions (free, 3 phrasings).
    suggestField: (input: {
      field: 'summary' | 'experience_bullets' | 'skills';
      language: 'ar' | 'en';
      currentValue?: string;
      experience?: { title?: string; company?: string };
      form?: {
        currentRole?: string;
        currentCompany?: string;
        yearsExperience?: number;
        skills?: string[];
        summary?: string;
      };
    }) => trpcMutation<{ suggestions: string[] }>('cv.suggestField', input),
  },
  campaign: {
    list: () => trpcQuery<any[]>('campaign.list'),
    get: (input: { id: string }) =>
      trpcQuery<{ campaign: any; recipients: any[] }>('campaign.get', input),
    previewMessages: (input: {
      senderRole: string;
      goal: string;
      tone?: 'professional' | 'friendly' | 'concise';
      language: 'ar' | 'en';
      companyIds: string[];
    }) => trpcMutation<{ messages: Array<{ companyId: string; companyName: string; subject: string; body: string }> }>('campaign.previewMessages', input),
    create: (input: {
      campaignName: string;
      senderRole: string;
      goal: string;
      tone?: 'professional' | 'friendly' | 'concise';
      language: 'ar' | 'en';
      companyIds: string[];
      dailyLimit?: number;
    }) => trpcMutation<any>('campaign.create', input),
    launch: (input: { id: string }) => trpcMutation<any>('campaign.launch', input),
    pause: (input: { id: string }) => trpcMutation<any>('campaign.pause', input),
    updateRecipient: (input: { recipientId: string; subject?: string; body?: string }) =>
      trpcMutation<any>('campaign.updateRecipient', input),
    processBatch: (input: { batchSize: number }) =>
      trpcMutation<{ processed: number; active: number; dryRun: boolean }>('campaign.processBatch', input),
  },
  companies: {
    list: (input?: { industry?: string; city?: string; size?: string; search?: string; limit?: number }) =>
      trpcQuery<any[]>('companies.list', input || {}),
    industries: () => trpcQuery<string[]>('companies.industries'),
    get: (input: { id: string }) => trpcQuery<any>('companies.get', input),
    create: (input: { name: string; name_ar?: string; website?: string; industry?: string; city?: string; size?: string; primary_email?: string }) =>
      trpcMutation<any>('companies.create', input),
    update: (input: { id: string; patch: Record<string, any> }) =>
      trpcMutation<any>('companies.update', input),
    enrich: (input: { id: string }) =>
      trpcMutation<{ company: any; emailsFound: number; note: string }>('companies.enrich', input),
    delete: (input: { id: string }) => trpcMutation<any>('companies.delete', input),
  },
  finance: {
    pulse: () => trpcQuery<{
      mrr: { today: number; lastMonth: number; spark: number[] };
      arr: { today: number; lastMonth: number; spark: number[] };
      newRevenue: { today: number; lastMonth: number; spark: number[] };
      churn: { today: number; lostMrr: number; lastMonth: number; spark: number[] };
      netMargin: { today: number; lastMonth: number; spark: number[] };
      cashOnHand: { today: number; lastMonth: number; spark: number[] };
    }>('finance.pulse'),
    waterfall: () => trpcQuery<{
      startingMrr: number; newMrr: number; expansionMrr: number; churnedMrr: number; endingMrr: number;
    }>('finance.waterfall'),
    planBreakdown: () => trpcQuery<{
      breakdown: Array<{ plan: string; users: number; mrr: number; percentOfMrr: number; avgTokensPerMonth: number; costPerUserSar: number; marginPercent: number | null }>;
      totalMrr: number;
    }>('finance.planBreakdown'),
    payments: (input?: { limit?: number }) => trpcQuery<{
      successful: any[]; failed: any[]; refunds: any[];
    }>('finance.payments', input || {}),
    costControl: () => trpcQuery<{
      apiBreakdown: Array<{ key: string; tokens: number; cost_usd: number; cost_sar: number }>;
      totalCostUsd: number; totalCostSar: number;
      topDrivers: Array<{ id: string; email: string | null; full_name: string | null; plan: string; tokens_consumed: number; cost_sar: number; revenue_sar: number; net_margin_sar: number }>;
      negativeMarginAlert: any | null;
    }>('finance.costControl'),
    exportCsv: (input: { month: string }) => trpcQuery<{
      csv: string; filename: string; totalRows: number; totalAmountSar: number; totalVatSar: number;
    }>('finance.exportCsv', input),
    updateSetting: (input: { key: 'cash_on_hand_sar' | 'usd_sar_rate' | 'apify_monthly_cost_usd' | 'infra_monthly_cost_usd'; value: number }) =>
      trpcMutation<{ success: boolean; key: string; value: number }>('finance.updateSetting', input),
    generateInvoice: (input: { userId: string; amountSar: number; description: string }) =>
      trpcMutation<{ paymentId: string; invoiceUrl: string; invoiceId: string }>('finance.generateInvoice', input),
    refundPayment: (input: { moyasarPaymentId: string; reason: string }) =>
      trpcMutation<{ success: boolean; paymentId: string; amountSar: number; note: string }>('finance.refundPayment', input),
  },
  admin: {
    stats: () => trpcQuery<any>('admin.stats'),
    users: (input?: { search?: string; limit?: number }) =>
      trpcQuery<any[]>('admin.users', input || {}),
    addTokens: (input: { userId: string; amount: number; reason: string }) =>
      trpcMutation<{ success: boolean; newBalance: number }>('admin.addTokens', input),
    adjustUserTokens: (input: { userId: string; amount: number; reason: string; wallet?: 'bonus' }) =>
      trpcMutation<{
        success: boolean;
        newBalance: number;
        newLegacyBalance: number;
        wallets: {
          bonus: { balance: number; expires_at: string | null; expired: boolean };
          subscription: { balance: number; renews_at: string | null };
          topup: { balance: number };
          total: number;
        };
      }>('admin.adjustUserTokens', input),
    listTokenTransactions: (input?: { userId?: string; limit?: number }) =>
      trpcQuery<{
        transactions: Array<{
          id: string;
          userId: string;
          userName: string | null;
          userEmail: string | null;
          wallet: 'bonus' | 'subscription' | 'topup';
          direction: 'credit' | 'debit';
          amount: number;
          operation: string;
          status: string;
          balanceAfter: number;
          reason: string | null;
          adminEmail: string | null;
          createdAt: string;
        }>;
      }>('admin.listTokenTransactions', input || {}),
    toggleBan: (input: { userId: string }) =>
      trpcMutation<{ success: boolean; banned: boolean }>('admin.toggleBan', input),
    campaigns: () => trpcQuery<any[]>('admin.campaigns'),
    systemStatus: () => trpcQuery<any>('admin.systemStatus'),
    dashboardOverview: () => trpcQuery<{
      signups: { today: number; yesterday: number; spark: number[] };
      activated: { today: number; yesterday: number; spark: number[] };
      paying: { today: number; yesterday: number; spark: number[] };
      mrr: { today: number; yesterday: number; spark: number[] };
      tokensBurned: { today: number; yesterday: number; spark: number[] };
      fires: { today: number; yesterday: number; spark: number[]; breakdown: { errors: number; banned: number; failedPayments: number } };
    }>('admin.dashboardOverview'),
    funnel: () => trpcQuery<{
      stages: Array<{ key: string; count: number }>;
      biggestDropIdx: number;
      biggestDropPct: number;
    }>('admin.funnel'),
    cohorts: () => trpcQuery<{
      hotProspects: Array<any>;
      churnRisk: Array<any>;
      heroes: Array<any>;
    }>('admin.cohorts'),
    tokenEconomy: () => trpcQuery<{
      burnByCategory: Array<{ key: string; tokens: number; cost_usd: number }>;
      top10Consumers: Array<any>;
      totalCostUSD: number;
      marginAlert: any | null;
    }>('admin.tokenEconomy'),
    growthSignals: () => trpcQuery<{
      activationRate30d: number;
      ttfvMinutes: number | null;
      kbExportRate: number;
      localeSplit: { ar: number; en: number; other: number };
      activationSpark: number[];
      signupsCount: number;
      activatedCount: number;
    }>('admin.growthSignals'),
  },
  reviews: {
    list: () => trpcQuery<any[]>('reviews.list'),
    submit: (input: { rating: number; comment: string }) =>
      trpcMutation<any>('reviews.submit', input),
    myReviews: () => trpcQuery<any[]>('reviews.myReviews'),
    listPending: () => trpcQuery<any[]>('reviews.listPending'),
    listAll: () => trpcQuery<any[]>('reviews.listAll'),
    approve: (input: { id: string }) => trpcMutation<any>('reviews.approve', input),
    reject: (input: { id: string }) => trpcMutation<any>('reviews.reject', input),
    delete: (input: { id: string }) => trpcMutation<any>('reviews.delete', input),
  },
  feedback: {
    submit: (input: { category: string; subject: string; description: string; priority?: string; pageUrl?: string }) =>
      trpcMutation<any>('feedback.submit', input),
    myTickets: () => trpcQuery<any[]>('feedback.myTickets'),
    listAll: () => trpcQuery<any[]>('feedback.listAll'),
    respond: (input: { id: string; response: string; status?: string }) =>
      trpcMutation<any>('feedback.respond', input),
    updateStatus: (input: { id: string; status: string }) =>
      trpcMutation<any>('feedback.updateStatus', input),
  },
  ops: {
    anthropicHealth: () => trpcQuery<{
      status: 'healthy' | 'error' | 'unreachable';
      httpCode: number;
      message: string;
      creditExhausted: boolean;
      latencyMs: number;
      timestamp: string;
    }>('ops.anthropicHealth'),
    pulse: () => trpcQuery<{
      signups: { today: number; yesterday: number; spark: number[] };
      activeSubs: { today: number; lastWeek: number; spark: number[] };
      expiringSoon: { today: number; lastWeek: number; spark: number[] };
      failedHooks: { today: number; yesterday: number; spark: number[] };
      apiErrors1h: { today: number; yesterday: number; spark: number[] };
      openIncidents: { today: number; yesterday: number; spark: number[]; signupsLastWeek: number };
    }>('ops.pulse'),
    signupFunnel: (input?: { days?: number }) =>
      trpcQuery<{ stages: Array<{ key: string; count: number }>; biggestDropIdx: number; biggestDropPct: number }>('ops.signupFunnel', input || {}),
    signupFeed: (input?: { limit?: number }) =>
      trpcQuery<{ events: Array<any>; abandonedLastHour: number }>('ops.signupFeed', input || {}),
    subscriptions: (input?: { status?: 'active' | 'expiring' | 'past_due' | 'canceled' | 'new_month'; limit?: number; offset?: number }) =>
      trpcQuery<{ rows: Array<any>; total: number }>('ops.subscriptions', input || {}),
    subscriptionAction: (input: { subscriptionId: string; action: 'extend' | 'cancel' | 'mark_paid'; days?: number }) =>
      trpcMutation<{ success: boolean; action: string }>('ops.subscriptionAction', input),
    servicesHealth: () => trpcQuery<{
      checkedAt: string;
      services: Array<{ key: string; status: 'healthy' | 'watch' | 'critical'; metric: any; last_checked: string }>;
    }>('ops.servicesHealth'),
    runHealthCheck: (input: { service: string }) =>
      trpcMutation<{ success: boolean; service: string }>('ops.runHealthCheck', input),
    webhooks: (input?: { limit?: number }) =>
      trpcQuery<{ rows: Array<any> }>('ops.webhooks', input || {}),
    retryFulfillment: (input: { paymentTransactionId: string }) =>
      trpcMutation<{ success: boolean; note: string }>('ops.retryFulfillment', input),
    crons: () => trpcQuery<{
      crons: Array<{ name: string; path: string; schedule: string; last_run: string | null; last_status: 'ok' | 'fail' | 'unknown' }>;
    }>('ops.crons'),
    triggerCron: (input: { endpoint: string }) =>
      trpcMutation<{ success: boolean; status: number; error?: string }>('ops.triggerCron', input),
    incidents: (input?: { status?: 'open' | 'investigating' | 'resolved' | 'dismissed' | 'all' }) =>
      trpcQuery<{ rows: Array<any> }>('ops.incidents', input || {}),
    createIncident: (input: { severity: 'info' | 'warning' | 'error' | 'critical'; title: string; description?: string; affected_service?: string }) =>
      trpcMutation<{ success: boolean; id: string }>('ops.createIncident', input),
    updateIncident: (input: { id: string; status: 'investigating' | 'resolved' | 'dismissed'; resolution_notes?: string }) =>
      trpcMutation<{ success: boolean }>('ops.updateIncident', input),
    broadcastEmail: (input: { subject_ar: string; subject_en: string; body_ar: string; body_en: string; audience: 'all' | 'paid' | 'free' }) =>
      trpcMutation<{ success: boolean; recipientCount: number; note: string }>('ops.broadcastEmail', input),
    toggleMaintenanceMode: (input: { enabled: boolean; message_ar?: string; message_en?: string }) =>
      trpcMutation<{ success: boolean; enabled: boolean }>('ops.toggleMaintenanceMode', input),
    registeredUsers: (input?: { search?: string; limit?: number; offset?: number }) =>
      trpcQuery<{
        rows: Array<{
          id: string;
          email: string;
          full_name: string;
          plan: string;
          token_balance: number;
          is_admin: boolean;
          created_at: string;
        }>;
        total: number;
      }>('ops.registeredUsers', input || {}),
    visitStats: (input?: { days?: number }) =>
      trpcQuery<{
        days: number;
        landingViews: number;
        pricingViews: number;
        uniqueLandingVisitors: number;
        signups: number;
        conversionRate: number;
        trackingSince: string | null;
      }>('ops.visitStats', input || {}),
  },
  aiFeedback: {
    submit: (input: { feature: string; outputId?: string; rating: number; comment?: string }) =>
      trpcMutation<{ id: string; success: boolean }>('aiFeedback.submit', input),
    list: (input?: { feature?: string; rating?: number; limit?: number }) =>
      trpcQuery<any[]>('aiFeedback.list', input || {}),
    stats: () => trpcQuery<Record<string, { total: number; avg: number; sum: number }>>('aiFeedback.stats'),
    listPrompts: (input?: { feature?: string }) =>
      trpcQuery<any[]>('aiFeedback.listPrompts', input || {}),
    savePrompt: (input: { feature: string; promptText: string; activate?: boolean }) =>
      trpcMutation<any>('aiFeedback.savePrompt', input),
    activatePrompt: (input: { promptId: string }) =>
      trpcMutation<any>('aiFeedback.activatePrompt', input),
  },
  executor: {
    list: () => trpcQuery<any[]>('executor.list'),
    startConversation: (input: { agentId: string; title?: string }) =>
      trpcMutation<any>('executor.startConversation', input),
    listConversations: () => trpcQuery<any[]>('executor.listConversations'),
    getConversation: (input: { conversationId: string }) =>
      trpcQuery<{ conversation: any; messages: any[]; actions: any[] }>('executor.getConversation', input),
    deleteConversation: (input: { conversationId: string }) =>
      trpcMutation<any>('executor.deleteConversation', input),
    sendMessage: (input: { conversationId: string; content: string }) =>
      trpcMutation<{ content: string; pendingActions: any[]; error?: string }>('executor.sendMessage', input),
    approveAction: (input: { actionId: string }) =>
      trpcMutation<any>('executor.approveAction', input),
    rejectAction: (input: { actionId: string }) =>
      trpcMutation<any>('executor.rejectAction', input),
    listActions: (input?: { conversationId?: string }) =>
      trpcQuery<any[]>('executor.listActions', input || {}),
  },
  analytics: {
    trackPageView: (input: { path: string; visitorId?: string; referrer?: string }) =>
      trpcMutation<{ ok: boolean }>('analytics.trackPageView', input),
    overview: (input: { range: 'today' | 'week' | 'month' | 'all' }) =>
      trpcQuery<{
        range: string;
        kpis: {
          profile_analyses: number;
          cvs_generated: number;
          posts_generated: number;
          campaigns_active: number;
          messages_sent: number;
          connections_accepted: number;
          response_rate: number;
          jobs_applied: number;
        };
        tokens: { balance: number; used_total: number };
      }>('analytics.overview', input),
    activityTimeseries: (input: { days: number }) =>
      trpcQuery<Array<{ date: string; analyses: number; cvs: number; posts: number }>>(
        'analytics.activityTimeseries',
        input
      ),
    campaignPerformance: () =>
      trpcQuery<Array<{
        id: string;
        name: string;
        status: string;
        prospects_count: number;
        sent: number;
        accepted: number;
        acceptance_rate: number;
      }>>('analytics.campaignPerformance'),
    prospectStatusDistribution: () =>
      trpcQuery<Array<{ status: string; count: number }>>(
        'analytics.prospectStatusDistribution'
      ),
    tokensBreakdown: (input: { range: 'today' | 'week' | 'month' | 'all' }) =>
      trpcQuery<Array<{ feature: string; total: number }>>(
        'analytics.tokensBreakdown',
        input
      ),
  },
  posts: {
    generate: (input: {
      topic: string;
      tones: string[];
      dialect: string;
      audience?: string;
      goal?: string;
      length?: 'short' | 'medium' | 'long';
      extras?: {
        hashtags?: boolean;
        callToAction?: boolean;
        emojis?: boolean;
        endingQuestion?: boolean;
        personalStory?: boolean;
      };
      useStyleSamples?: boolean;
      inspirationUrl?: string;
    }) =>
      trpcMutation<{
        id: string;
        dna: {
          topic: string;
          tones: string[];
          dialect: string;
          language: string;
          audience: string;
          goal: string;
          length: string;
          dnaScore: number;
        };
        variations: Array<{
          id: 'safe' | 'balanced' | 'bold';
          label: string;
          content: string;
          charCount: number;
          hook: string;
          hashtags: string[];
        }>;
        tips: string[];
        tokensUsed: number;
      }>('posts.generate', input),
    selectVariation: (input: {
      postId: string;
      variationId: 'safe' | 'balanced' | 'bold';
    }) => trpcMutation<{ success: boolean }>('posts.selectVariation', input),
    addStyleSample: (input: { content: string }) =>
      trpcMutation<{ id: string; styleAnalysis: any }>(
        'posts.addStyleSample',
        input
      ),
    listStyleSamples: () =>
      trpcQuery<
        Array<{
          id: string;
          content: string;
          style_analysis: any;
          created_at: string;
        }>
      >('posts.listStyleSamples'),
    deleteStyleSample: (input: { id: string }) =>
      trpcMutation<{ success: boolean }>('posts.deleteStyleSample', input),
    previewInspiration: (input: { url: string }) =>
      trpcMutation<{
        source: 'youtube' | 'article' | 'text';
        title: string;
        preview: string;
      }>('posts.previewInspiration', input),
    list: () => trpcQuery<any[]>('posts.list'),
    delete: (input: { id: string }) =>
      trpcMutation<{ success: boolean }>('posts.delete', input),
    update: (input: { id: string; patch: Record<string, any> }) =>
      trpcMutation<any>('posts.update', input),
  },
  document: {
    parse: (input: { fileBase64: string; fileName: string; mimeType: string }) =>
      trpcMutation<{
        text: string;
        method: 'pdf-text' | 'pdf-ocr' | 'docx' | 'image-ocr' | 'text';
        documentType: 'pdf' | 'docx' | 'image' | 'text';
        pageCount: number;
        confidence: number;
        warnings: string[];
        textLength: number;
      }>('document.parse', input),
  },
  pricing: {
    getPlans: () => trpcQuery<Array<{
      id: string;
      name_ar: string; name_en: string;
      tagline_ar: string | null; tagline_en: string | null;
      monthly_price_sar: string; annual_price_sar: string | null;
      monthly_tokens: number;
      display_order: number;
      is_featured: boolean; is_custom: boolean; is_free: boolean;
      badge_ar: string | null; badge_en: string | null;
      features: Array<{
        id: string; plan_id: string; feature_key: string;
        feature_ar: string; feature_en: string;
        is_included: boolean; is_coming_soon: boolean; is_highlighted: boolean;
        display_order: number;
      }>;
    }>>('pricing.getPlans'),
    getProducts: () => trpcQuery<Array<{
      id: string;
      name_ar: string; name_en: string;
      description_ar: string | null; description_en: string | null;
      price_sar: string;
      category: string;
      token_cost: number;
      is_bundle: boolean;
      bundle_items: any | null;
      display_order: number;
    }>>('pricing.getProducts'),
    createGuestAccount: (input: { fullName: string; phone: string; email: string }) =>
      trpcMutation<
        | { kind: 'existing_email'; email: string }
        | {
            kind: 'created';
            userId: string;
            session: { access_token: string; refresh_token: string };
          }
      >('pricing.createGuestAccount', input),
    getCurrentSubscription: () => trpcQuery<any | null>('pricing.getCurrentSubscription'),
    getTokenBalance: () => trpcQuery<{
      balance: number; totalPurchased: number; totalUsed: number; lastUpdated: string | null;
    }>('pricing.getTokenBalance'),
    getTransactionHistory: (input?: { limit?: number; offset?: number }) =>
      trpcQuery<{ tokenTransactions: any[]; paymentTransactions: any[] }>(
        'pricing.getTransactionHistory', input || {}
      ),
    subscribeToPlan: (input: { planId: string; billingCycle: 'monthly' | 'annual'; promoCode?: string }) =>
      trpcMutation<{
        paymentId: string; amount: number; currency: 'SAR';
        planId: string; billingCycle: 'monthly' | 'annual';
        muyassarCheckoutUrl: string | null;
      }>('pricing.subscribeToPlan', input),
    // Preview a promo code's discounted first-month price (server-authoritative).
    previewPromo: (input: { code: string; planId: string }) =>
      trpcQuery<
        | { valid: false }
        | { valid: true; code: string; discountPercent: number; originalAmount: number; discountedAmount: number }
      >('pricing.previewPromo', input),
    cancelSubscription: () => trpcMutation<{ success: boolean }>('pricing.cancelSubscription'),
    purchaseTokens: (input: { quantity: number }) =>
      trpcMutation<{
        paymentId: string; amount: number; currency: 'SAR';
        quantity: number; muyassarCheckoutUrl: string | null;
      }>('pricing.purchaseTokens', input),
    purchaseProduct: (input: { productId: string }) =>
      trpcMutation<{
        paymentId: string; amount: number; currency: 'SAR';
        productId: string; muyassarCheckoutUrl: string | null;
      }>('pricing.purchaseProduct', input),
    getPaymentStatus: (input: { transactionId: string }) =>
      trpcQuery<{
        id: string;
        status: 'pending' | 'completed' | 'failed' | 'cancelled' | string;
        type: 'subscription' | 'token_topup' | 'product' | string;
        amount_sar: string | number;
        currency: string;
        completed_at: string | null;
        metadata: Record<string, any> | null;
        reference_id: string | null;
        reference_type: string | null;
        wallet_credited?: 'bonus' | 'subscription' | 'topup' | null;
        tokens_credited?: number | null;
      }>('pricing.getPaymentStatus', input),
    // Public verify-on-return — works without a session (id is the capability).
    // Recovers a missing/late webhook by checking Moyasar directly, then
    // returns minimal, non-sensitive status. Used by /v2/checkout/success.
    verifyCheckout: (input: { transactionId: string }) =>
      trpcMutation<{
        status: 'pending' | 'completed' | 'failed' | 'cancelled' | 'refunded' | string;
        type: 'plan_subscription' | 'token_topup' | 'product' | string;
        amountSar: number;
        currency: string;
        tokensCredited: number | null;
        walletCredited: 'bonus' | 'subscription' | 'topup' | null;
        bonusGranted: number;
        planNameAr: string | null;
        planNameEn: string | null;
        authenticated: boolean;
        ownedByCaller: boolean;
      }>('pricing.verifyCheckout', input),
    // ─── Sprint 7 endpoints (3-wallet aware) ─────────────────────────
    listPlans: () => trpcQuery<Array<{
      id: string;
      name_ar: string; name_en: string;
      tagline_ar: string | null; tagline_en: string | null;
      monthly_price_sar: number; annual_price_sar: number | null;
      monthly_tokens: number;
      display_order: number;
      is_featured: boolean; is_custom: boolean; is_free: boolean;
      badge_ar: string | null; badge_en: string | null;
      features: Array<{
        feature_key: string; feature_ar: string; feature_en: string;
        is_included: boolean; is_coming_soon: boolean; is_highlighted: boolean;
      }>;
    }>>('pricing.listPlans'),
    listTopupPackages: () => trpcQuery<Array<{
      code: string;
      name_ar: string; name_en: string;
      tokens: number; price_sar: number;
      description_ar: string | null; description_en: string | null;
      badge_ar: string | null; badge_en: string | null;
      display_order: number;
    }>>('pricing.listTopupPackages'),
    getSubscriptionState: () => trpcQuery<{
      planId: string;
      planNameAr: string; planNameEn: string;
      monthlyTokens: number;
      status: 'active' | 'trialing' | 'cancelled' | 'past_due' | 'expired' | 'free';
      currentPeriodStart: string | null;
      currentPeriodEnd: string | null;
      cancelAtPeriodEnd: boolean;
      autoRenew: boolean;
      billingCycle: 'monthly' | 'annual' | null;
      scheduledDowngradeTo: string | null;
      isFirst: boolean;
    }>('pricing.getSubscriptionState'),
    getWalletSnapshot: () => trpcQuery<{
      bonus: { balance: number; expires_at: string | null };
      subscription: { balance: number; renews_at: string | null; plan_code: string | null };
      topup: { balance: number };
      total: number;
    }>('pricing.getWalletSnapshot'),
    createSubscriptionCheckout: (input: { planId: string; billingCycle: 'monthly' | 'annual' }) =>
      trpcMutation<{
        paymentId: string; amount: number; currency: 'SAR';
        planId: string; billingCycle: 'monthly' | 'annual';
        muyassarCheckoutUrl: string | null;
      }>('pricing.createSubscriptionCheckout', input),
    createTopupCheckout: (input: { packageCode: string }) =>
      trpcMutation<{
        paymentId: string; amount: number; currency: 'SAR';
        tokens: number; packageCode: string;
        muyassarCheckoutUrl: string | null;
      }>('pricing.createTopupCheckout', input),
    upgradeSubscription: (input: { newPlanId: string }) =>
      trpcMutation<{ proratedTokensAdded: number; fromPlan: string; toPlan: string }>(
        'pricing.upgradeSubscription', input,
      ),
    downgradeSubscription: (input: { newPlanId: string }) =>
      trpcMutation<{ fromPlan: string; toPlan: string; appliesAt: string | null }>(
        'pricing.downgradeSubscription', input,
      ),
    cancelSubscriptionV2: (input?: { immediate?: boolean }) =>
      trpcMutation<{ ok: true; effectiveAt: string | null }>(
        'pricing.cancelSubscriptionV2', input || {},
      ),
    reactivateSubscription: () =>
      trpcMutation<{ ok: true }>('pricing.reactivateSubscription', {}),
    getPaymentHistory: (input?: { limit?: number }) =>
      trpcQuery<Array<{
        id: string;
        amount_sar: number; currency: string;
        type: string; status: string;
        payment_method: string | null;
        metadata: Record<string, unknown> | null;
        wallet_credited: 'bonus' | 'subscription' | 'topup' | null;
        tokens_credited: number | null;
        created_at: string; completed_at: string | null;
      }>>('pricing.getPaymentHistory', input || {}),
    estimateProration: (input: { newPlanId: string }) =>
      trpcQuery<{
        fromPlan: string; toPlan: string;
        currentTokens: number; newTokens: number;
        proratedTokens: number; daysRemaining: number;
      }>('pricing.estimateProration', input),
    getGoalBonuses: () =>
      trpcQuery<Array<{
        id: string; amount_tokens: number;
        plan_at_grant: string;
        granted_at: string; expires_at: string;
        status: 'active' | 'consumed' | 'expired' | 'revoked';
      }>>('pricing.getGoalBonuses'),
  },
  faris: {
    listAgents: () => trpcQuery<Array<{
      id: string; name_ar: string; name_en: string; role_ar: string; role_en: string;
      portal: string; approval_mode: 'approval_required' | 'suggest_only' | 'auto_with_bounds' | 'auto';
      argue_mode_enabled: boolean; monthly_token_budget: number; is_active: boolean;
      avatar_color: string; avatar_icon: string;
      tokens_this_month: number; cost_sar_this_month: number;
      pending_tasks: number; completed_this_month: number; over_budget: boolean;
    }>>('faris.listAgents'),
    getApprovalQueue: (input?: { filter?: 'pending' | 'edited' | 'executing' | 'all'; agentId?: string; limit?: number }) =>
      trpcQuery<{ rows: any[] }>('faris.getApprovalQueue', input || {}),
    getTask: (input: { taskId: string }) =>
      trpcQuery<{ task: any; arguments: any[] }>('faris.getTask', input),
    approveTask: (input: { taskId: string; editedPayload?: Record<string, any> }) =>
      trpcMutation<{ success: boolean; taskId: string }>('faris.approveTask', input),
    rejectTask: (input: { taskId: string; reason: string }) =>
      trpcMutation<{ success: boolean }>('faris.rejectTask', input),
    replyToArgue: (input: { taskId: string; message: string }) =>
      trpcMutation<{ agentReply: string | null; note?: string }>('faris.replyToArgue', input),
    morningBrief: () =>
      trpcQuery<{ signups: number; paid: number; pending: number; adSpendSar: number; generatedAt: string }>('faris.morningBrief'),
    dailyVitals: () =>
      trpcQuery<{
        signupsToday: number; paidToday: number; mrrSar: number; adSpendSar: number;
        pendingApprovals: number; agentTokenSpendSar: number; errors24h: number; churnedThisMonth: number;
      }>('faris.dailyVitals'),
    agentCostReport: (input?: { agentId?: string; days?: number }) =>
      trpcQuery<{
        sinceDays: number;
        breakdown: Array<{ agentId: string; tokens: number; calls: number; costUsd: number; costSar: number }>;
        totalCostSar: number; totalCostUsd: number;
      }>('faris.agentCostReport', input || {}),
    readContextFile: () => trpcQuery<{ content: string }>('faris.readContextFile'),
    updateContextFile: (input: { content: string }) =>
      trpcMutation<{ success: boolean }>('faris.updateContextFile', input),
    toggleAgentMode: (input: { agentId: string; mode: 'approval_required' | 'suggest_only' | 'auto_with_bounds' | 'auto' }) =>
      trpcMutation<{ success: boolean }>('faris.toggleAgentMode', input),
    updateAgentBudget: (input: { agentId: string; monthlyTokenBudget: number }) =>
      trpcMutation<{ success: boolean }>('faris.updateAgentBudget', input),
  },
  sayed: {
    generateMonthlyBatch: (input: { platforms: string[]; themes?: string[]; postsPerPlatform?: number }) =>
      trpcMutation<{ tasksCreated: number; totalEstimatedCostSar: number }>('sayed.generateMonthlyBatch', input),
    draftSinglePost: (input: { platform: string; topic: string; sourceUrl?: string }) =>
      trpcMutation<{ taskId: string }>('sayed.draftSinglePost', input),
    draftAdCampaign: (input: { channel: string; objective: string; dailyBudgetSar: number; targetAudience: string }) =>
      trpcMutation<{ campaignTaskId: string; creativeVariants: number }>('sayed.draftAdCampaign', input),
    listContentCalendar: (input?: { startDate?: string; endDate?: string; platform?: string }) =>
      trpcQuery<{ items: any[] }>('sayed.listContentCalendar', input || {}),
    listAdCampaigns: (input?: { status?: string }) =>
      trpcQuery<{ campaigns: any[] }>('sayed.listAdCampaigns', input || {}),
    publishApprovedContent: (input: { contentId: string }) =>
      trpcMutation<{ success: boolean; note: string }>('sayed.publishApprovedContent', input),
    killCampaign: (input: { campaignId: string; reason: string }) =>
      trpcMutation<{ success: boolean }>('sayed.killCampaign', input),
    repurposeFromRss: (input: { feedUrl: string }) =>
      trpcMutation<{ itemsRead: number; tasksQueued: number }>('sayed.repurposeFromRss', input),
  },
  alMukhadram: {
    draftWelcomeSequence: () => trpcMutation<{ tasksCreated: number; sequenceId: string | null }>('alMukhadram.draftWelcomeSequence', {}),
    draftDailyRescues: (input?: { limit?: number }) => trpcMutation<{ tasksCreated: number; cohorts: any }>('alMukhadram.draftDailyRescues', input || {}),
    draftSupportReply: (input: { userId: string; inboundMessage: string; channel: 'whatsapp' | 'email' }) =>
      trpcMutation<{ taskId: string }>('alMukhadram.draftSupportReply', input),
    flagVips: () => trpcMutation<{ count: number }>('alMukhadram.flagVips', {}),
    listEnrollments: (input?: { status?: string; sequenceName?: string }) => trpcQuery<any[]>('alMukhadram.listEnrollments', input || {}),
    enrollUser: (input: { userId: string; sequenceName: string }) => trpcMutation<any>('alMukhadram.enrollUser', input),
    exitUser: (input: { userId: string; sequenceName: string; reason?: string }) => trpcMutation<any>('alMukhadram.exitUser', input),
    healthScore: (input: { userId: string }) => trpcMutation<any>('alMukhadram.healthScore', input),
    recomputeAllScores: (input?: { limit?: number }) => trpcMutation<{ updated: number }>('alMukhadram.recomputeAllScores', input || {}),
    healthCohorts: () => trpcQuery<Record<string, number>>('alMukhadram.healthCohorts'),
    listHealthScores: (input?: { segment?: string; limit?: number }) => trpcQuery<any[]>('alMukhadram.listHealthScores', input || {}),
    listWhatsappMessages: (input?: { userId?: string; limit?: number }) => trpcQuery<any[]>('alMukhadram.listWhatsappMessages', input || {}),
    listEmailMessages: (input?: { userId?: string; limit?: number }) => trpcQuery<any[]>('alMukhadram.listEmailMessages', input || {}),
    sendApprovedMessage: (input: { taskId: string }) => trpcMutation<{ sent: number; errors: string[] }>('alMukhadram.sendApprovedMessage', input),
  },
  hassan: {
    draftHotUpgradePitches: (input?: { limit?: number; minPropensity?: number }) =>
      trpcMutation<{ tasksCreated: number }>('hassan.draftHotUpgradePitches', input || {}),
    draftPitchForUser: (input: { userId: string; trigger: string; surface: string; experimentId?: string }) =>
      trpcMutation<{ pitchId: string | null; taskId: string | null }>('hassan.draftPitchForUser', input),
    listPendingPitches: () => trpcQuery<any[]>('hassan.listPendingPitches'),
    approvePitch: (input: { pitchId: string }) => trpcMutation<{ ok: boolean }>('hassan.approvePitch', input),
    rejectPitch: (input: { pitchId: string }) => trpcMutation<{ ok: boolean }>('hassan.rejectPitch', input),
    listExperiments: (input?: { status?: string }) => trpcQuery<any[]>('hassan.listExperiments', input || {}),
    proposeExperiment: (input: { surface: string; hypothesis: string }) =>
      trpcMutation<{ experimentId: string | null; taskId: string }>('hassan.proposeExperiment', input),
    startExperiment: (input: { experimentId: string }) => trpcMutation<{ ok: boolean }>('hassan.startExperiment', input),
    killExperiment: (input: { experimentId: string; reason?: string }) => trpcMutation<{ ok: boolean }>('hassan.killExperiment', input),
    hotLeads: (input?: { limit?: number }) => trpcQuery<any[]>('hassan.hotLeads', input || {}),
    referralCodes: (input?: { userId?: string }) => trpcQuery<any[]>('hassan.referralCodes', input || {}),
    createReferralCode: (input?: { userId?: string; rewardInviter?: number; rewardInvitee?: number; maxUses?: number }) =>
      trpcMutation<{ code: string }>('hassan.createReferralCode', input || {}),
    redeemReferralCode: (input: { code: string; inviteeUserId: string }) =>
      trpcMutation<any>('hassan.redeemReferralCode', input),
    servePitch: (input: { surface: string }) => trpcQuery<any>('hassan.servePitch', input),
    recordPitchClick: (input: { pitchId: string }) => trpcMutation<{ ok: boolean }>('hassan.recordPitchClick', input),
    recordConversion: (input: { userId: string; amountSar: number; pitchId?: string }) =>
      trpcMutation<{ ok: boolean }>('hassan.recordConversion', input),
  },
  fatima: {
    detectFrictionPatterns: (input?: { lookbackDays?: number }) =>
      trpcMutation<{ patternsFound: number }>('fatima.detectFrictionPatterns', input || {}),
    listFrictionPatterns: (input?: { severity?: string; status?: string }) =>
      trpcQuery<any[]>('fatima.listFrictionPatterns', input || {}),
    acknowledgePattern: (input: { patternId: string }) => trpcMutation<{ ok: boolean }>('fatima.acknowledgePattern', input),
    dismissPattern: (input: { patternId: string; reason?: string }) => trpcMutation<{ ok: boolean }>('fatima.dismissPattern', input),
    generateWeeklyReport: () => trpcMutation<{ reportId: string | null }>('fatima.generateWeeklyReport', {}),
    latestWeeklyReport: () => trpcQuery<any>('fatima.latestWeeklyReport'),
    computeFunnel: (input: { feature: string; startDate: string; endDate: string }) =>
      trpcMutation<{ stages: Array<{ name: string; users: number }> }>('fatima.computeFunnel', input),
    digestUserVoice: () => trpcMutation<{ themes: any[] }>('fatima.digestUserVoice', {}),
    captureEvent: (input: { event: string; properties?: any; distinctId?: string; sessionId?: string; pageUrl?: string }) =>
      trpcMutation<{ ok: boolean }>('fatima.captureEvent', input),
  },
  dhai: {
    scanNewSignup: (input: { userId: string }) => trpcMutation<any>('dhai.scanNewSignup', input),
    moderateContent: (input: { contentId: string; contentType: string; scannedText: string; language?: string; sourceAgent?: string }) =>
      trpcMutation<{ decision: 'approved' | 'flagged' | 'blocked'; violations: string[] }>('dhai.moderateContent', input),
    checkLinkedinTos: (input: { text: string }) => trpcMutation<{ ok: boolean; hits: string[] }>('dhai.checkLinkedinTos', input),
    listFraudSignals: (input?: { status?: string; severity?: string; limit?: number }) =>
      trpcQuery<any[]>('dhai.listFraudSignals', input || {}),
    reviewSignal: (input: { signalId: string; decision: 'confirmed_fraud' | 'false_positive' | 'resolved'; notes?: string }) =>
      trpcMutation<{ ok: boolean }>('dhai.reviewSignal', input),
    logPdplEvent: (input: { eventType: string; userId?: string; dataCategory?: string; details?: any }) =>
      trpcMutation<{ ok: boolean }>('dhai.logPdplEvent', input),
    pdplAuditLog: (input?: { userId?: string; eventType?: string; limit?: number }) =>
      trpcQuery<any[]>('dhai.pdplAuditLog', input || {}),
    contentModerationLog: (input?: { decision?: 'approved' | 'flagged' | 'blocked'; limit?: number }) =>
      trpcQuery<any[]>('dhai.contentModerationLog', input || {}),
    dailySweep: () => trpcMutation<{ openSignals: number; flaggedContent: number; pdplEvents: number }>('dhai.dailySweep', {}),
  },
  hussein: {
    listKnownPatterns: () => trpcQuery<any[]>('hussein.listKnownPatterns'),
    seedDefaultPatterns: () => trpcMutation<{ seeded: number }>('hussein.seedDefaultPatterns', {}),
    autoResolveErrors: () => trpcMutation<{ scanned: number; resolved: number; novel: number }>('hussein.autoResolveErrors', {}),
    servicesHealthCheck: () => trpcMutation<Record<string, any>>('hussein.servicesHealthCheck', {}),
    recentResolutions: (input?: { limit?: number }) => trpcQuery<any[]>('hussein.recentResolutions', input || {}),
    createIncident: (input: { error: string; severity: 'low' | 'medium' | 'high' | 'critical'; service?: string }) =>
      trpcMutation<{ incidentId: string | null }>('hussein.createIncident', input),
  },
  mohammed: {
    reconcileMoyasarDaily: () => trpcMutation<{ checked: number; new: number; mismatches: number }>('mohammed.reconcileMoyasarDaily', {}),
    generateInvoice: (input: { paymentTransactionId: string }) =>
      trpcMutation<{ invoiceId: string; invoiceNumber: string; qrBase64: string } | null>('mohammed.generateInvoice', input),
    listInvoices: (input?: { status?: string; userId?: string; limit?: number }) =>
      trpcQuery<any[]>('mohammed.listInvoices', input || {}),
    myInvoices: () => trpcQuery<any[]>('mohammed.myInvoices'),
    dailySnapshot: () => trpcMutation<{ snapshotId: string | null }>('mohammed.dailySnapshot', {}),
    financeKpis: (input?: { days?: number }) => trpcQuery<any[]>('mohammed.financeKpis', input || {}),
    predictRunway: () => trpcMutation<{ runwayDays: number; dailyBurnSar: number; cashSar: number }>('mohammed.predictRunway', {}),
    marginAlerts: () => trpcMutation<{ alerts: any[] }>('mohammed.marginAlerts', {}),
    weeklyReport: () => trpcMutation<{ summary: string }>('mohammed.weeklyReport', {}),
  },
  agents: {
    list: () => trpcQuery<any[]>('agents.list'),
    startConversation: (input: { agentId: string; title?: string }) =>
      trpcMutation<any>('agents.startConversation', input),
    listConversations: (input?: { agentId?: string }) =>
      trpcQuery<any[]>('agents.listConversations', input || {}),
    getConversation: (input: { conversationId: string }) =>
      trpcQuery<{ conversation: any; messages: any[] }>('agents.getConversation', input),
    sendMessage: (input: { conversationId: string; content: string }) =>
      trpcMutation<{ content: string; tokensUsed: number }>('agents.sendMessage', input),
    deleteConversation: (input: { conversationId: string }) =>
      trpcMutation<any>('agents.deleteConversation', input),
    listTrainingNotes: (input: { agentId: string }) =>
      trpcQuery<any[]>('agents.listTrainingNotes', input),
    addTrainingNote: (input: { agentId: string; note: string }) =>
      trpcMutation<any>('agents.addTrainingNote', input),
    deleteTrainingNote: (input: { noteId: string }) =>
      trpcMutation<any>('agents.deleteTrainingNote', input),
  },
  careerProfile: {
    // No arg → raw canonical (AuthGate / Settings rely on this). Pass
    // { section: 'radar' } to get the override-merged view for read-only
    // surfaces so a transient radar role-change reflects (#10).
    get: (input?: { section?: 'radar' | 'resume' | 'content' }) =>
      trpcQuery<{ profile: any | null }>('careerProfile.get', input ?? {}),
    wallets: () => trpcQuery<{
      bonus:        { balance: number; expires_at: string | null; expired: boolean };
      subscription: { balance: number; renews_at:  string | null };
      topup:        { balance: number };
      total:        number;
    }>('careerProfile.wallets'),
    // Career Brain enrichment review (Constitution §3.2): what the uploaded CV
    // can add to the still-empty Brain fields. Read-only; apply via update().
    brainPreview: () => trpcQuery<{
      hasUpload: boolean;
      fileName: string | null;
      suggestions: Array<{
        field: 'manual_about' | 'manual_top_skills' | 'manual_current_role' | 'manual_years_experience' | 'manual_education';
        labelAr: string;
        labelEn: string;
        preview: string;
        value: string | string[] | number;
      }>;
      alreadyKnown: string[];
      stillMissing: string[];
    }>('careerProfile.brainPreview'),
    // Brain Snapshot (Constitution §3.1): "what Wassel knows about you" —
    // read-only aggregation across profile + CV + analysis. `readiness` is
    // transport-only; the UI renders it as a STATE, never a raw number.
    brainSnapshot: () => trpcQuery<{
      told: {
        goal: string | null; level: string | null; targetRole: string | null;
        industry: string | null; primaryLanguage: string | null;
        currentRole: string | null; yearsExperience: number | null;
        skillsCount: number; projectsCount: number;
      };
      cv: {
        hasUpload: boolean; fileName: string | null;
        experienceCount: number; certificationCount: number; languageCount: number;
        achievementCount: number; skillsCount: number;
        currentCompany: string | null; location: string | null;
      };
      analysis: { readinessKnown: boolean; readiness: number | null; linkedinConnected: boolean };
    }>('careerProfile.brainSnapshot'),
    create: (input: {
      goal: 'job_search' | 'promotion' | 'personal_brand' | 'opportunities' | 'career_change';
      level: 'entry' | 'mid' | 'senior' | 'executive';
      target_role: string;
      industry: string;
      primary_language: 'ar' | 'en';
      linkedin_url?: string | null;
      manual_about?: string | null;
      manual_top_skills?: string[] | null;
      manual_current_role?: string | null;
      manual_years_experience?: number | null;
      manual_education?: string | null;
      // Unified Profile (Phase 1)
      top_projects?: Array<{ title: string; description: string; impact: string }> | null;
      skip_extra?: boolean;
    }) => trpcMutation<{ profile: any }>('careerProfile.create', input),
    update: (input: Record<string, unknown>) =>
      trpcMutation<{ profile: any }>('careerProfile.update', input),
    // Unified Profile (Phase 1): stamp that the user skipped the optional
    // enrichment step, so we never re-ask (R02).
    skipExtra: () => trpcMutation<{ profile: any }>('careerProfile.skipExtra', {}),
    delete: () => trpcMutation<{ success: boolean }>('careerProfile.delete', {}),
    listOverrides: () => trpcQuery<{ overrides: any[] }>('careerProfile.listOverrides'),
    sectionOverride: (input: {
      section: 'radar' | 'resume' | 'content';
      payload: Record<string, unknown>;
      expires_in_hours?: number;
    }) => trpcMutation<{ override: any }>('careerProfile.sectionOverride', input),
    deleteOverride: (input: { id: string }) =>
      trpcMutation<{ success: boolean }>('careerProfile.deleteOverride', input),
    export: () => trpcMutation<{ data: Record<string, unknown> }>('careerProfile.export', {}),
    deleteAllData: () =>
      trpcMutation<{ success: boolean; errors: string[] }>('careerProfile.deleteAllData', { confirm: true }),
  },
  warRoom: {
    getPersonalities: () =>
      trpcQuery<{ personalities: Array<{
        agent_id: string;
        age: number;
        speech_style_ar: string; speech_style_en: string;
        catchphrases_ar: string[] | null; catchphrases_en: string[] | null;
        expressions: string[];
        default_expression: string;
        table_seat: number;
        voice_pitch: number; voice_rate: number;
        signature_animation: string | null;
        system_prompt_extension_ar: string; system_prompt_extension_en: string;
      }> }>('warRoom.getPersonalities'),
    startSession: (input: { language?: 'ar' | 'en'; voiceEnabled?: boolean }) =>
      trpcMutation<{ sessionId: string }>('warRoom.startSession', input),
    endSession: (input: { sessionId: string }) =>
      trpcMutation<{ success: boolean }>('warRoom.endSession', input),
    sessionStats: () =>
      trpcQuery<{ totalSessions: number; totalMessages: number; totalDecisions: number }>('warRoom.sessionStats'),
    morningBrief: (input: { sessionId: string; language?: 'ar' | 'en' }) =>
      trpcMutation<{ briefs: Array<{ agentId: string; message: string; expression: string; turnId: string }> }>('warRoom.morningBrief', input),
    sendMessage: (input: { sessionId: string; message: string; language?: 'ar' | 'en'; voiceEnabled?: boolean }) =>
      trpcMutation<{
        aliTurnId: string;
        replies: Array<{ agentId: string; message: string; expression: string; turnId: string }>;
      }>('warRoom.sendMessage', input),
    listMessages: (input: { sessionId: string; limit?: number }) =>
      trpcQuery<{ turns: Array<{
        id: string; speaker_type: 'ali' | 'agent'; speaker_id: string | null;
        message: string; language: 'ar' | 'en'; expression: string | null; created_at: string;
      }> }>('warRoom.listMessages', input),
    recordDecision: (input: {
      agentId: string;
      conversationId?: string | null;
      decisionType: 'approve' | 'reject' | 'edit' | 'approve_with_changes' | 'ask_question' | 'defer';
      originalProposal: string;
      aliResponse?: string;
      aliEdit?: string;
      rejectionReason?: string;
      topicTags?: string[];
    }) => trpcMutation<{ id: string }>('warRoom.recordDecision', input),
    getDecisionMemory: (input: { agentId: string; limit?: number; topicTags?: string[] }) =>
      trpcQuery<{ decisions: Array<{
        decision_type: string;
        original_proposal: string;
        ali_response: string | null;
        ali_edit: string | null;
        topic_tags: string[] | null;
        created_at: string;
      }> }>('warRoom.getDecisionMemory', input),
    agentMemoryStats: () =>
      trpcQuery<{ counts: Array<{ agentId: string; count: number }> }>('warRoom.agentMemoryStats'),
    attachScreenContent: (input: {
      conversationId: string;
      contentType: 'chart' | 'table' | 'text' | 'image' | 'funnel' | 'kpi' | 'comparison';
      titleAr?: string;
      titleEn?: string;
      payload: Record<string, unknown>;
      displayDurationSeconds?: number;
    }) => trpcMutation<{ id: string }>('warRoom.attachScreenContent', input),
    getScreenContent: (input: { conversationId: string }) =>
      trpcQuery<{ content: Array<{
        id: string;
        conversation_id: string;
        content_type: string;
        title_ar: string | null;
        title_en: string | null;
        payload: any;
        display_duration_seconds: number;
        created_at: string;
      }> }>('warRoom.getScreenContent', input),
    weeklyJournal: (input?: { weekStart?: string }) =>
      trpcQuery<{ journal: {
        id: string;
        week_start: string;
        observations_ar: string;
        observations_en: string;
        patterns_detected: any;
        decisions_analyzed: number;
        created_at: string;
      } | null }>('warRoom.weeklyJournal', input || {}),
    generateWeeklyJournal: (input?: { language?: 'ar' | 'en' }) =>
      trpcMutation<{ id: string; created: boolean }>('warRoom.generateWeeklyJournal', input || {}),
  },
  radar: {
    preflight: () => trpcQuery<{
      ready: boolean;
      profile: {
        target_role: string;
        industry: string;
        linkedin_url: string | null;
        primary_language: 'ar' | 'en';
      } | null;
      cost: number;
      hasCache: boolean;
      latestCacheId: string | null;
      latestCachedAt: string | null;
      triggers: Array<{ type: string; metadata?: Record<string, unknown> }>;
      // "Act as if" experiment: when set, profile.target_role is the OVERRIDDEN
      // role; canonicalTargetRole is the user's permanent one. Lets the entry
      // page flag the experiment + offer a one-tap exit (radar.clearOverride).
      activeOverride: { id: string; targetRole: string } | null;
      canonicalTargetRole: string;
    }>('radar.preflight'),
    run: (input?: { overrideTargetRole?: string; language?: 'ar' | 'en'; forceRefresh?: boolean }) =>
      trpcMutation<{
        cacheId: string;
        analysisId: string;
        isCacheHit: boolean;
        tokensCharged: number;
        walletUsed: 'bonus' | 'subscription' | 'topup' | 'mixed' | null;
        result: RadarResultShape;
      }>('radar.run', input ?? {}),
    getCached: (input?: { cacheId?: string }) =>
      trpcQuery<{
        cacheId: string;
        targetRole: string;
        language: 'ar' | 'en';
        result: RadarResultShape;
        fixesUnlocked: boolean;
        currentScore: number;
        targetScore: number;
        sourceLinkedinUrl: string | null;
        hitCount: number;
        createdAt: string;
        lastAccessedAt: string;
      }>('radar.getCached', input ?? {}),
    // M2 — spend 149 tokens to reveal the ready-made fix rewrites on a free
    // diagnostic. Idempotent: a repeat call returns the fixes for 0 tokens.
    unlockFixes: (input: { cacheId: string; language?: 'ar' | 'en' }) =>
      trpcMutation<{
        cacheId: string;
        tokensCharged: number;
        walletUsed: 'bonus' | 'subscription' | 'topup' | 'mixed' | null;
        alreadyUnlocked: boolean;
        result: RadarResultShape;
      }>('radar.unlockFixes', input),
    history: (input?: { limit?: number }) =>
      trpcQuery<{
        analyses: Array<{
          id: string;
          cache_id: string | null;
          target_role: string;
          is_cache_hit: boolean;
          tokens_charged: number;
          wallet_used: string | null;
          current_score: number | null;
          target_score: number | null;
          language: 'ar' | 'en';
          duration_ms: number | null;
          created_at: string;
        }>;
      }>('radar.history', input ?? {}),
    applyFix: (input: { cacheId: string; fixIndex: number }) =>
      trpcMutation<{ success: boolean; appliedFixId: string }>('radar.applyFix', input),
    revertFix: (input: { appliedFixId: string }) =>
      trpcMutation<{ success: boolean }>('radar.revertFix', input),
    refreshTriggers: () =>
      trpcQuery<{
        hasTriggered: boolean;
        triggers: Array<{ type: string; metadata?: Record<string, unknown> }>;
      }>('radar.refreshTriggers'),
    markTriggerActedUpon: (input: { triggerId: string }) =>
      trpcMutation<{ success: boolean }>('radar.markTriggerActedUpon', input),
    sessionOverride: (input: { targetRole: string; expiresInHours?: number }) =>
      trpcMutation<{ override: { id: string; section: string; payload: Record<string, unknown>; expires_at: string } }>(
        'radar.sessionOverride',
        input,
      ),
    clearOverride: (input: { overrideId: string }) =>
      trpcMutation<{ success: boolean }>('radar.clearOverride', input),
  },
  resume: {
    preflight: (input?: { language?: 'ar' | 'en' }) =>
      trpcQuery<{
        ready: boolean;
        profile: {
          target_role: string;
          industry: string;
          level: 'entry' | 'mid' | 'senior' | 'executive';
          linkedin_url: string | null;
          primary_language: 'ar' | 'en';
        } | null;
        recommendedTemplate: ResumeTemplateShape | null;
        alternativeTemplates: ResumeTemplateShape[];
        templateReasons: Record<string, TemplateReasonCode[]>;
        hasCache: boolean;
        latestCacheId: string | null;
        latestVersionId: string | null;
        estimatedCost: number;
        fullBuildCost: number;
        activeVersionsCount: number;
        archivedCount: number;
        legacyCount: number;
      }>('resume.preflight', input ?? {}),
    listVersions: (input?: { status?: 'active' | 'archived' | 'legacy' | 'all'; limit?: number }) =>
      trpcQuery<{ versions: ResumeVersionRow[] }>('resume.listVersions', input ?? {}),
    getVersion: (input: { versionId: string }) =>
      trpcQuery<{
        version: ResumeVersionRow & { cache_id: string | null };
        cache: {
          id: string;
          result: ResumeShape;
          ats_score: number;
          ats_breakdown: ResumeAtsBreakdownShape;
          created_at: string;
          template_id: string;
        } | null;
        refinementsUsed: number;
        freeRefinementsPerVersion: number;
        paidRefinementCost: number;
      }>('resume.getVersion', input),
    getCached: (input: { cacheId: string }) =>
      trpcQuery<{
        cacheId: string;
        targetRole: string;
        templateId: string;
        language: 'ar' | 'en';
        result: ResumeShape;
        atsScore: number;
        atsBreakdown: ResumeAtsBreakdownShape;
        isFullBuild: boolean;
        parentResumeId: string | null;
        hitCount: number;
        createdAt: string;
        lastAccessedAt: string;
      }>('resume.getCached', input),
    listTemplates: (input?: { language?: 'ar' | 'en' }) =>
      trpcQuery<{ templates: ResumeTemplateShape[] }>('resume.listTemplates', input ?? {}),
    recommendTemplate: (input?: { overrideTargetRole?: string; language?: 'ar' | 'en' }) =>
      trpcQuery<{
        primary: ResumeTemplateShape;
        alternatives: ResumeTemplateShape[];
        reasons: Record<string, TemplateReasonCode[]>;
      }>(
        'resume.recommendTemplate',
        input ?? {},
      ),
    build: (input: {
      templateId: string;
      overrideTargetRole?: string;
      language?: 'ar' | 'en';
      forceRefresh?: boolean;
      useUploadedCv?: boolean;
      // V2 audit point 9: the manual form data (the single UI build entry).
      manual?: Partial<CVManualFormData>;
    }) => trpcMutation<{
      cacheId: string;
      versionId: string;
      isCacheHit: boolean;
      tokensCharged: number;
      walletUsed: 'bonus' | 'subscription' | 'topup' | 'mixed' | null;
      atsScore: number;
      atsBreakdown: ResumeAtsBreakdownShape;
      result: ResumeShape;
    }>('resume.build', input),
    createNewVersion: (input: {
      parentCacheId: string;
      newTargetRole: string;
      templateId: string;
      language?: 'ar' | 'en';
    }) => trpcMutation<{
      cacheId: string;
      versionId: string;
      isCacheHit: boolean;
      tokensCharged: number;
      walletUsed: 'bonus' | 'subscription' | 'topup' | 'mixed' | null;
      atsScore: number;
      atsBreakdown: ResumeAtsBreakdownShape;
      result: ResumeShape;
    }>('resume.createNewVersion', input),
    refine: (input: {
      versionId: string;
      chipType: string;
      customPrompt?: string;
      targetSection?: string;
      language?: 'ar' | 'en';
    }) => trpcMutation<{
      result: ResumeShape;
      atsScore: number;
      atsBreakdown: ResumeAtsBreakdownShape;
      tokensCharged: number;
      refinementIndex: number;
      isFreeWindow: boolean;
      remainingFree: number;
      cacheId: string;
    }>('resume.refine', input),
    archive: (input: { versionId: string }) =>
      trpcMutation<{ success: boolean }>('resume.archive', input),
    restore: (input: { versionId: string }) =>
      trpcMutation<{ success: boolean }>('resume.restore', input),
    exportPdf: (input: { versionId: string }) =>
      trpcMutation<{ filename: string; mimeType: string; base64: string }>('resume.exportPdf', input),
    exportDocx: (input: { versionId: string }) =>
      trpcMutation<{ filename: string; mimeType: string; base64: string }>('resume.exportDocx', input),
    diagnose: (input?: {
      overrideTargetRole?: string;
      language?: 'ar' | 'en';
      upload?: { text: string; filename?: string };
    }) => trpcMutation<ResumeDiagnosticShape>('resume.diagnose', input ?? {}),
    latestDiagnostic: () =>
      trpcQuery<
        | { exists: false }
        | {
            exists: true;
            atsScore: number;
            expectedScore: number;
            projectionComputed: boolean;
            targetRole: string;
            source: 'linkedin' | 'upload';
            createdAt: string;
          }
      >('resume.latestDiagnostic', {}),
    rescoreVersion: (input: { versionId: string }) =>
      trpcMutation<{ atsScore: number; atsBreakdown: ResumeAtsBreakdownShape }>('resume.rescoreVersion', input),
    history: (input?: { limit?: number }) =>
      trpcQuery<{ versions: ResumeVersionRow[] }>('resume.history', input ?? {}),
    sessionOverride: (input: { targetRole: string; expiresInHours?: number }) =>
      trpcMutation<{ override: { id: string; section: string; payload: Record<string, unknown>; expires_at: string } }>(
        'resume.sessionOverride',
        input,
      ),
  },
  content: {
    preflight: (input: {
      contentType: ContentTypeShape;
      topic?: string;
      sourcePostId?: string;
      language?: 'ar' | 'en';
    }) => trpcQuery<{
      hasCareerProfile: boolean;
      profile: {
        goal: string;
        level: string;
        target_role: string;
        industry: string;
        primary_language: 'ar' | 'en';
      } | null;
      contentType: ContentTypeShape;
      estimatedCost: number;
      hasCacheHit: boolean;
      latestCacheId: string | null;
      latestVersionId: string | null;
      suggestions: TopicSuggestionShape[];
      activeContentCount: number;
      archivedCount: number;
      legacyCount: number;
    }>('content.preflight', input),
    listVersions: (input?: {
      contentType?: ContentTypeShape;
      status?: 'active' | 'archived' | 'published_externally' | 'legacy' | 'all';
      limit?: number;
    }) => trpcQuery<{ versions: ContentVersionRow[] }>('content.listVersions', input ?? {}),
    getVersion: (input: { versionId: string }) =>
      trpcQuery<{
        version: ContentVersionRow & { cache_id: string | null };
        cache: {
          id: string;
          result: ContentResultShape;
          content_type: ContentTypeShape;
          topic: string;
          source_post_id: string | null;
          created_at: string;
          expires_at: string;
          hit_count: number;
        } | null;
        refinementsUsed: number;
        freeRefinementsPerVersion: number;
        paidRefinementCost: number;
        pendingReminder: {
          id: string;
          remind_at: string;
          status: string;
          notification_channel: string[];
        } | null;
        rebuildsUsed: number;
        rebuildsRemaining: number;
        includedRebuilds: number;
      }>('content.getVersion', input),
    getCached: (input: { cacheId: string }) =>
      trpcQuery<{
        cacheId: string;
        contentType: ContentTypeShape;
        topic: string;
        sourcePostId: string | null;
        language: 'ar' | 'en';
        result: ContentResultShape;
        tokensCharged: number;
        hitCount: number;
        createdAt: string;
        lastAccessedAt: string;
        expiresAt: string;
      }>('content.getCached', input),
    generatePost: (input: { topic: string; language?: 'ar' | 'en'; forceRefresh?: boolean; brief?: string }) =>
      trpcMutation<ContentGenerateResultShape>('content.generatePost', input),
    generateCarousel: (input: { topic: string; language?: 'ar' | 'en'; forceRefresh?: boolean; brief?: string; slidesCount?: number; sourcePostId?: string }) =>
      trpcMutation<ContentGenerateResultShape>('content.generateCarousel', input),
    generateRepurpose: (input: { sourcePostId?: string; sourceText?: string; language?: 'ar' | 'en'; forceRefresh?: boolean }) =>
      trpcMutation<ContentGenerateResultShape>('content.generateRepurpose', input),
    // V2 audit point 20: feedback rebuild (3 included per paid generation).
    rebuildWithFeedback: (input: { versionId: string; feedback: string; language?: 'ar' | 'en' }) =>
      trpcMutation<{
        newVersionId: string;
        newCacheId: string;
        result: ContentResultShape;
        rebuildsUsed: number;
        rebuildsRemaining: number;
        includedRebuilds: number;
        tokensCharged: number;
      }>('content.rebuildWithFeedback', input),
    // V2 audit point 22: pending reminders for scheduled badges + the calendar.
    pendingReminders: () =>
      trpcQuery<{ reminders: Array<{ id: string; version_id: string; remind_at: string; notification_channel: string[] }> }>(
        'content.pendingReminders',
      ),
    refine: (input: { versionId: string; chipType: string; customPrompt?: string; language?: 'ar' | 'en' }) =>
      trpcMutation<{
        result: ContentResultShape;
        tokensCharged: number;
        refinementIndex: number;
        isFreeWindow: boolean;
        remainingFree: number;
        cacheId: string;
      }>('content.refine', input),
    archive: (input: { versionId: string }) =>
      trpcMutation<{ success: boolean }>('content.archive', input),
    restore: (input: { versionId: string }) =>
      trpcMutation<{ success: boolean }>('content.restore', input),
    markPublished: (input: { versionId: string; externalUrl?: string }) =>
      trpcMutation<{ success: boolean }>('content.markPublished', input),
    setReminder: (input: { versionId: string; remindAt: string; channels?: Array<'in_app' | 'email'> }) =>
      trpcMutation<{ reminderId: string }>('content.setReminder', input),
    dismissReminder: (input: { reminderId: string }) =>
      trpcMutation<{ success: boolean }>('content.dismissReminder', input),
    exportCarouselPdf: (input: { versionId: string }) =>
      trpcMutation<{ filename: string; mimeType: string; base64: string }>('content.exportCarouselPdf', input),
    // V2 audit point 23d: one designed 1080x1080 PNG per slide.
    exportCarouselSlide: (input: { versionId: string; slideIndex: number }) =>
      trpcMutation<{ filename: string; mimeType: string; base64: string; slidesTotal: number }>('content.exportCarouselSlide', input),
    topicSuggestions: (input?: { language?: 'ar' | 'en'; regenerate?: boolean }) =>
      trpcQuery<{ suggestions: TopicSuggestionShape[]; profileHash: string; isCacheHit: boolean }>(
        'content.topicSuggestions',
        input ?? {},
      ),
    history: (input?: { limit?: number }) =>
      trpcQuery<{ versions: ContentVersionRow[] }>('content.history', input ?? {}),
  },
  dashboard: {
    getCareerPulse: (input?: { language?: 'ar' | 'en' }) =>
      trpcQuery<CareerPulseShape>('dashboard.getCareerPulse', input ?? {}),
    getNextTask: (input?: { language?: 'ar' | 'en' }) =>
      trpcQuery<{ task: AiSuggestionShape | null }>('dashboard.getNextTask', input ?? {}),
    getReadiness: (input?: { language?: 'ar' | 'en' }) =>
      trpcQuery<ReadinessShape>('dashboard.getReadiness', input ?? {}),
    getAllSuggestions: (input?: {
      status?: 'active' | 'dismissed' | 'acted_upon' | 'expired' | 'all';
      limit?: number;
    }) => trpcQuery<{ suggestions: AiSuggestionShape[] }>('dashboard.getAllSuggestions', input ?? {}),
    dismissSuggestion: (input: { suggestionId: string }) =>
      trpcMutation<{ success: boolean }>('dashboard.dismissSuggestion', input),
    acknowledgeSuggestion: (input: { suggestionId: string }) =>
      trpcMutation<{ success: boolean }>('dashboard.acknowledgeSuggestion', input),
    getActivityFeed: (input?: {
      pillar?:
        | 'onboarding'
        | 'radar'
        | 'resume'
        | 'content'
        | 'profile'
        | 'wallet'
        | 'dashboard'
        | 'system';
      days?: number;
      limit?: number;
    }) =>
      trpcQuery<{ entries: ActivityLogEntryShape[] }>(
        'dashboard.getActivityFeed',
        input ?? {},
      ),
    getDrafts: () =>
      trpcQuery<DraftSummaryShape>('dashboard.getDrafts'),
    getSufficesFor: () =>
      trpcQuery<SufficesForShape>('dashboard.getSufficesFor'),
    regenerateSuggestions: (input?: { language?: 'ar' | 'en' }) =>
      trpcMutation<{ generated: number; throttled: boolean; message?: string }>(
        'dashboard.regenerateSuggestions',
        input ?? {},
      ),
    markVisited: () =>
      trpcMutation<VisitResultShape>('dashboard.markVisited', {}),
    logActivity: (input: {
      action: string;
      pillar?:
        | 'onboarding'
        | 'radar'
        | 'resume'
        | 'content'
        | 'profile'
        | 'wallet'
        | 'dashboard'
        | 'system';
      target?: string;
      payload?: Record<string, unknown>;
      relatedResourceType?: string;
      relatedResourceId?: string;
      tokensCharged?: number;
      language?: 'ar' | 'en';
    }) => trpcMutation<{ success: boolean }>('dashboard.logActivity', input),
    companion: {
      getState: () =>
        trpcQuery<CompanionStateShape>('dashboard.companion.getState'),
      getWelcome: (input?: { language?: 'ar' | 'en' }) =>
        trpcQuery<{ message: string | null }>('dashboard.companion.getWelcome', input ?? {}),
      markWelcomed: () =>
        trpcMutation<{ success: boolean }>('dashboard.companion.markWelcomed', {}),
      markTourDone: () =>
        trpcMutation<{ success: boolean }>('dashboard.companion.markTourDone', {}),
      getMessage: (input?: { language?: 'ar' | 'en' }) =>
        trpcQuery<CompanionMessageShape>('dashboard.companion.getMessage', input ?? {}),
      recordSignal: (input: {
        signalType: 'page_view' | 'action' | 'visit';
        route?: string;
        payload?: Record<string, unknown>;
      }) => trpcMutation<{ success: boolean }>('dashboard.companion.recordSignal', input),
    },
  },
  // «🎯 خطتي للوصول إلى هدفي» (ADR-006). One read-only endpoint — the living,
  // on-the-fly plan computed from existing signals. States, never numbers.
  plan: {
    get: (input?: { language?: 'ar' | 'en' }) =>
      trpcQuery<CareerPlanShape>('plan.get', input ?? {}),
  },
  notifications: {
    list: (input?: { status?: 'unread' | 'all'; limit?: number; offset?: number }) =>
      trpcQuery<{ notifications: NotificationRowShape[] }>('notifications.list', input ?? {}),
    unreadCount: () =>
      trpcQuery<{ count: number }>('notifications.unreadCount'),
    markAsRead: (input: { notificationId: string }) =>
      trpcMutation<{ success: boolean }>('notifications.markAsRead', input),
    markAllAsRead: () =>
      trpcMutation<{ count: number }>('notifications.markAllAsRead', {}),
    dismiss: (input: { notificationId: string }) =>
      trpcMutation<{ success: boolean }>('notifications.dismiss', input),
    getPreferences: () =>
      trpcQuery<{ preferences: NotificationPreferencesShape }>('notifications.getPreferences'),
    updatePreferences: (input: {
      emailEnabled?: boolean;
      inAppEnabled?: boolean;
      marketingEmailsEnabled?: boolean;
      language?: 'ar' | 'en';
      quietHoursStart?: string | null;
      quietHoursEnd?: string | null;
      timezone?: string;
    }) => trpcMutation<{ success: boolean }>('notifications.updatePreferences', input),
    markAppOpened: () =>
      trpcMutation<{ success: boolean }>('notifications.markAppOpened', {}),
    testNotification: (input?: { channel?: 'in_app' | 'email' | 'both'; language?: 'ar' | 'en' }) =>
      trpcMutation<{ queued: boolean; skipped?: boolean; reason?: string; notificationId?: string }>(
        'notifications.testNotification',
        input ?? {},
      ),
  },
  beta: {
    redeemCode: (input: { code: string }) =>
      trpcMutation<{ success: boolean; error?: string; granted_tokens?: number; granted_plan?: string; granted_months?: number }>('beta.redeemCode', input),
    submitFeedback: (input: { pillar: string; nps: number; what_worked?: string; what_didnt?: string }) =>
      trpcMutation<{ success: boolean }>('beta.submitFeedback', input),
    createCode: (input: { code: string; cohort?: string; granted_plan: string; granted_tokens?: number; granted_months?: number; max_redemptions?: number; notes?: string; expires_in_days?: number }) =>
      trpcMutation<unknown>('beta.createCode', input),
    listCodes: () => trpcQuery<unknown[]>('beta.listCodes'),
    listFeedback: (input?: { pillar?: string; limit?: number }) =>
      trpcQuery<unknown[]>('beta.listFeedback', input ?? {}),
    getMetrics: () => trpcQuery<{ total_redemptions: number; total_feedback: number; avg_nps: number; promoters: number; passives: number; detractors: number; nps_score: number }>('beta.getMetrics'),
  },
  // ─── Support / customer-service chat (Part 2 UI consumes Part 1 backend) ──
  support: {
    /** Public — saved FAQ entries for the sequence UI. Zero AI cost. */
    faqList: (input?: { audience?: 'all' | 'visitor' | 'user' }) =>
      trpcQuery<{
        faqs: Array<{
          id: string;
          question_ar: string; question_en: string;
          answer_ar: string;   answer_en: string;
          display_order: number;
        }>;
      }>('support.faqList', input ?? {}),
    /** Public — create a conversation (visitor or, if authed, user). */
    startConversation: (input?: { visitorId?: string }) =>
      trpcMutation<{
        conversationId: string;
        mode: 'visitor' | 'user';
        status: 'active' | 'awaiting_admin' | 'closed';
        aiReplyCount: number;
        allowExtended: boolean;
      }>('support.startConversation', input ?? {}),
    /** Public — core endpoint: FAQ-first → capped AI → handoff (server-enforced). */
    sendMessage: (input: { conversationId: string; message: string }) =>
      trpcMutation<{
        conversationId: string;
        source: 'faq' | 'ai' | 'handoff';
        reply: string;
        language: 'ar' | 'en';
        aiReplyCount: number;
        status: 'active' | 'awaiting_admin' | 'closed';
        faqId?: string | null;
        capReached: boolean;
      }>('support.sendMessage', input),
    caps: () => trpcQuery<{ visitor: number; userDefault: number; userExtended: number }>('support.caps'),
    admin: {
      listConversations: (input?: { status?: 'active' | 'awaiting_admin' | 'closed'; limit?: number }) =>
        trpcQuery<{
          conversations: Array<{
            id: string;
            mode: 'visitor' | 'user';
            user_id: string | null;
            status: 'active' | 'awaiting_admin' | 'closed';
            ai_reply_count: number;
            allow_extended: boolean;
            last_message_at: string;
            created_at: string;
          }>;
        }>('support.admin.listConversations', input ?? {}),
      getConversation: (input: { conversationId: string }) =>
        trpcQuery<{
          conversation: {
            id: string;
            mode: 'visitor' | 'user';
            user_id: string | null;
            status: 'active' | 'awaiting_admin' | 'closed';
            ai_reply_count: number;
            allow_extended: boolean;
            visitor_id: string | null;
            last_message_at: string;
            created_at: string;
            updated_at: string;
          };
          messages: Array<{
            id: string;
            role: 'user' | 'assistant' | 'admin';
            source: 'user' | 'faq' | 'ai' | 'handoff' | 'admin';
            content: string;
            faq_id: string | null;
            created_at: string;
          }>;
        }>('support.admin.getConversation', input),
      setAllowExtended: (input: { conversationId: string; allow: boolean }) =>
        trpcMutation<{
          conversationId: string;
          allowExtended: boolean;
          status: 'active' | 'awaiting_admin' | 'closed';
        }>('support.admin.setAllowExtended', input),
    },
  },
  coverLetter: {
    generate: (input: {
      jobDescription: string;
      targetCompany?: string;
      language?: 'ar' | 'en';
    }) =>
      trpcMutation<{
        content: { greeting: string; paragraphs: string[]; signature: string };
        pdfBase64: string | null;
        docxBase64: string | null;
        tokensCharged: number;
        tokensRemaining: number;
        contextUsed: {
          hasCareerProfile: boolean;
          hasResumeContent: boolean;
          hasRadarStrengths: boolean;
        };
      }>('coverLetter.generate', input),
  },
};

// ─── Notification shapes (Sprint 8) ─────────────────────────────────
export type NotificationRowShape = {
  id: string;
  user_id: string;
  channel: 'in_app' | 'email' | 'both';
  category: 'system' | 'engagement' | 'transactional' | 'marketing';
  template_key: string;
  title_ar: string; title_en: string;
  body_ar: string;  body_en: string;
  cta_label_ar: string | null;
  cta_label_en: string | null;
  cta_url: string | null;
  metadata: Record<string, unknown>;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  status: 'pending' | 'sent' | 'delivered' | 'failed' | 'cancelled' | 'read' | 'dismissed';
  scheduled_for: string;
  sent_at: string | null;
  read_at: string | null;
  dismissed_at: string | null;
  created_at: string;
};

export type NotificationPreferencesShape = {
  user_id: string;
  email_enabled: boolean;
  in_app_enabled: boolean;
  marketing_emails_enabled: boolean;
  system_emails_enabled: boolean;
  language: 'ar' | 'en';
  quiet_hours_start: string | null;
  quiet_hours_end: string | null;
  timezone: string;
  daily_email_count: number;
  daily_count_reset_at: string;
  last_email_sent_at: string | null;
  last_app_opened_at: string | null;
  updated_at: string;
};

// ─────────────────────────────────────────────
// Resume shapes — mirror server/_core/lib/resume-engine.ts so the client
// bundle doesn't pull in the engine itself.
// ─────────────────────────────────────────────

/**
 * The manual CV-builder form shape (/v2/cvs/new → "fill manually"). Matches the
 * `userData` object `cv.generate` accepts on the server, so the form state can be
 * sent straight through. Also the return shape of `cv.getAutofill` (as Partial).
 */
export type CVManualExperience = {
  title: string; company: string; location: string;
  startDate: string; endDate: string; bullets: string[];
};
export type CVManualEducation = {
  school: string; degree: string; field: string; year: string; achievements: string[];
};
export type CVManualCert = { name: string; issuer: string; year: string };
export type CVManualLang = { name: string; proficiency: string };
/** V2 audit point 13: document types a user can attach to feed the builds. */
export type UploadedDocType = 'cv' | 'certificate' | 'course' | 'letter' | 'other';

export type CVManualFormData = {
  fullName: string;
  email: string;
  phone: string;
  location: string;
  linkedinUrl: string;
  currentRole: string;
  currentCompany: string;
  yearsExperience: number;
  summary: string;
  skills: string[];
  languages: CVManualLang[];
  education: CVManualEducation[];
  experience: CVManualExperience[];
  certifications: CVManualCert[];
  achievements: string[];
};

export type ResumeVersionRow = {
  id: string;
  cache_id: string | null;
  target_role: string;
  display_name: string;
  template_id: string;
  status: 'active' | 'archived' | 'legacy';
  ats_score: number | null;
  tokens_charged: number;
  wallet_used: 'bonus' | 'subscription' | 'topup' | 'mixed' | null;
  language: 'ar' | 'en';
  legacy_source: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
};

export type ResumeTemplateShape = {
  id: string;
  display_name_ar: string;
  display_name_en: string;
  description_ar: string | null;
  description_en: string | null;
  layout_type: 'classic' | 'modern' | 'creative' | 'executive';
  region_fit: string[];
  language_fit: string[];
  level_fit: string[];
  industry_boost: string[] | null;
  is_active: boolean;
  preview_url: string | null;
};

export type ResumeAtsBreakdownShape = {
  keywords: number;
  sections: number;
  format: number;
  quantified: number;
  matched_keywords: string[];
  missing_keywords: string[];
  issues: string[];
};

// M3 — the deterministic reason codes recommendTemplate attaches per template.
export type TemplateReasonCode =
  | 'level-match'
  | 'region-match'
  | 'global-fallback'
  | 'language-match'
  | 'industry-boost';

// M3 — internal target-profile benchmark (you vs ideal-for-role).
export type TargetBenchmarkShape = {
  you: number;
  ideal: number;
  gap: number;
  level: string;
  components: Array<{
    key: 'keywords' | 'sections' | 'format' | 'quantified';
    you: number;
    ideal: number;
    max: number;
  }>;
};

// M3 — free ATS diagnostic result (0 tokens, deterministic).
export type ResumeDiagnosticShape = {
  source: 'linkedin' | 'upload';
  atsScore: number;
  atsBreakdown: ResumeAtsBreakdownShape;
  expectedScore: number;
  /** True only when expectedScore is a REAL silently-generated resume score. */
  projectionComputed: boolean;
  benchmark: TargetBenchmarkShape;
  targetRole: string;
  language: 'ar' | 'en';
  diagnosticId: string;
  isCacheHit: boolean;
};

export type ResumeShape = {
  header: {
    name: string;
    title: string;
    location: string | null;
    phone: string | null;
    email: string | null;
    linkedin_url: string | null;
  };
  summary: string;
  experience: Array<{
    role: string;
    company: string;
    location: string | null;
    start: string;
    end: string;
    bullets: string[];
  }>;
  education: Array<{
    degree: string;
    institution: string;
    graduated: string;
    honors: string | null;
  }>;
  skills: { hard: string[]; soft: string[] };
  certifications: Array<{ name: string; issuer: string; year: string }>;
  languages: Array<{ name: string; proficiency: string }>;
  meta: {
    target_role: string;
    template_id: string;
    profile_hash: string;
    language: 'ar' | 'en';
    version_label: string;
    generated_at: string;
  };
};

// ─────────────────────────────────────────────
// Content shapes — mirror server/_core/lib/content-engine.ts
// ─────────────────────────────────────────────

export type ContentTypeShape = 'post' | 'carousel' | 'repurpose_bundle';

export type PostShape = {
  body: string;
  hashtags: string[];
  language: 'ar' | 'en';
  topic: string;
  meta: { generated_at: string; content_type: 'post' };
};

export type CarouselShape = {
  slides: Array<{ title: string; body: string; image_prompt: string | null }>;
  caption: string;
  hashtags: string[];
  language: 'ar' | 'en';
  topic: string;
  meta: { generated_at: string; content_type: 'carousel' };
};

export type RepurposeBundleShape = {
  source_post_id: string;
  carousel: {
    slides: Array<{ title: string; body: string; image_prompt: string | null }>;
    caption: string;
    hashtags: string[];
  };
  short_video_script: {
    hook: string;
    beats: string[];
    cta: string;
  };
  follow_up_post: {
    body: string;
    hashtags: string[];
  };
  language: 'ar' | 'en';
  meta: { generated_at: string; content_type: 'repurpose_bundle' };
};

/**
 * V2 audit point 25: repurpose v2 — ONE post rebuilt in the user's voice from
 * someone else's pasted post. Same content_type as the legacy bundle.
 */
export type RepurposeV2Shape = {
  body: string;
  hashtags: string[];
  source_excerpt?: string;
  language: 'ar' | 'en';
  topic?: string;
  meta: { generated_at: string; content_type: 'repurpose_bundle' };
};

export type ContentResultShape = PostShape | CarouselShape | RepurposeBundleShape | RepurposeV2Shape;

export type TopicSuggestionShape = {
  topic: string;
  recommended_type: ContentTypeShape;
  reason: string;
};

export type ContentVersionRow = {
  id: string;
  cache_id: string | null;
  content_type: ContentTypeShape;
  display_title: string;
  topic: string;
  status: 'active' | 'archived' | 'published_externally' | 'legacy';
  tokens_charged: number;
  wallet_used: 'bonus' | 'subscription' | 'topup' | 'mixed' | null;
  language: 'ar' | 'en';
  legacy_source: string | null;
  external_url: string | null;
  archived_at: string | null;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  /** Point 23e: joined cache link (a cross-sell carousel carries its source post). */
  cache?: { source_post_id: string | null } | null;
};

export type ContentGenerateResultShape = {
  cacheId: string;
  versionId: string;
  isCacheHit: boolean;
  tokensCharged: number;
  walletUsed: 'bonus' | 'subscription' | 'topup' | 'mixed' | null;
  result: ContentResultShape;
  toneViolations: string[];
};

/**
 * Shape of the RadarResult returned by `radar.run` and `radar.getCached`.
 * Mirrors server/_core/lib/radar-engine.ts RadarResultSchema. Kept here
 * (rather than imported) so the client bundle doesn't pull in the engine.
 */
/** The 8 Radar diagnostic dimensions (mirrors engine DIMENSION_KEYS). */
export type RadarDimensionKey =
  | 'headline' | 'about' | 'experience' | 'skills'
  | 'keywords' | 'activity' | 'education' | 'completeness';

export type RadarDimensionShape = {
  key: RadarDimensionKey;
  current: number;
  target: number;
  gap: number;
  found: string[];
  missing: string[];
  unmeasured?: boolean;
};

export type RadarResultShape = {
  strengths: Array<{ title: string; detail: string; dimension?: RadarDimensionKey }>;
  gaps: Array<{ title: string; detail: string; severity: 'low' | 'medium' | 'high'; dimension?: RadarDimensionKey }>;
  included_fixes: Array<{
    title: string;
    field: 'headline' | 'about' | 'experience' | 'skills';
    suggestion: string;
    rationale: string;
    impact_weight: number;
    dimension?: RadarDimensionKey;
    // M2: true when the ready-made rewrite is gated behind the 149-token unlock.
    // When locked, suggestion/rationale arrive empty.
    locked?: boolean;
  }>;
  suggested_actions: Array<{
    title: string;
    detail: string;
    pillar: 'resume' | 'content' | 'profile';
    deeplink: string;
  }>;
  quick_wins: Array<{
    title: string;
    impact: 'high' | 'medium' | 'low';
    effort: 'very_low' | 'low' | 'medium' | 'high';
    area: string;
    score: number;
  }>;
  // M2: deterministic per-dimension sub-scores. Absent on pre-M2 cached rows
  // (the UI falls back to legacy strengths/gaps lists then).
  dimensions?: RadarDimensionShape[];
  // Task C: Success Simulator — engine-computed cumulative projection. Absent on
  // pre-Phase-B rows AND when there are no fixes to project (UI shows nothing).
  // The UI only DISPLAYS these numbers; it never recomputes them (#26).
  simulator?: {
    base: number;
    cap: number;
    steps: Array<{
      title: string;
      field: 'headline' | 'about' | 'experience' | 'skills';
      cumulative: number;
    }>;
  };
  meta: {
    current_score: number;
    target_score: number;
    target_role: string;
    profile_hash: string;
    language: 'ar' | 'en';
    generated_at: string;
  };
};

// ─────────────────────────────────────────────
// Dashboard shapes — mirror server/_core/lib/dashboard-engine.ts so the
// client bundle doesn't pull in the engine itself.
// ─────────────────────────────────────────────

export type ReadinessStateShape = 'none' | 'radar_only' | 'resume_only' | 'both';

export type NextBestActionShape = {
  task: AiSuggestionShape | null;
  pointGain: number | null;
  effort: 'very_low' | 'low' | 'medium' | 'high' | null;
  effortMinutes: number | null;
  source: 'radar_quick_win' | 'pillar_estimate' | null;
};

export type ReadinessShape = {
  /** Unified current readiness (0..100), or null when no signal exists. */
  readiness: number | null;
  /** Unified target readiness (0..100), or null when no signal exists. */
  target: number | null;
  breakdown: {
    radar: number | null;
    ats: number | null;
  };
  state: ReadinessStateShape;
  /** The #1 action + its projected Readiness point-gain. */
  nextBestAction: NextBestActionShape;
};

export type CareerPulseShape = {
  radarScore: number | null;
  radarLastUpdated: string | null;
  radarScoreDelta30d: number | null;
  resumeCount: number;
  activeResumeAtsScore: number | null;
  resumeLastUpdated: string | null;
  contentCount30d: number;
  contentLastPostedAt: string | null;
  walletSummary: {
    bonus: number;
    subscription: number;
    topup: number;
    total: number;
    bonusExpiresAt: string | null;
  };
  streakDays: number;
  latestQuickWins: Array<{
    title: string;
    impact: 'high' | 'medium' | 'low';
    effort: 'very_low' | 'low' | 'medium' | 'high';
    cacheId: string;
  }>;
};

export type AiSuggestionShape = {
  id: string;
  user_id: string;
  suggestion_type: 'next_task' | 'quick_win' | 'reminder' | 'opportunity' | null;
  pillar: 'radar' | 'resume' | 'content' | 'profile' | 'wallet' | null;
  headline: string;
  rationale: string;
  cta_label: string | null;
  cta_url: string;
  cta_payload: Record<string, unknown> | null;
  score: number;
  priority_score: number | null;
  language: 'ar' | 'en';
  status: 'active' | 'dismissed' | 'acted_upon' | 'expired';
  expires_at: string | null;
  acted_upon_at: string | null;
  dismissed_at: string | null;
  computed_for_date: string;
  created_at: string;
};

export type CompanionStateShape = {
  user_id: string;
  welcomed_at: string | null;
  tour_done_at: string | null;
  welcome_message: string | null;
  welcome_message_lang: 'ar' | 'en' | null;
  visit_count: number;
  created_at: string;
  updated_at: string;
};

export type PurchaseGuidanceShape = {
  needed: number;
  balance: number;
  shortfall: number;
  pillar: 'radar' | 'resume' | 'content';
  cta_url: '/v2/pricing';
};

export type CompanionMessageShape = {
  task: AiSuggestionShape | null;
  guidance: PurchaseGuidanceShape | null;
};

export type ActivityLogEntryShape = {
  id: number;
  user_id: string;
  action: string;
  pillar:
    | 'onboarding'
    | 'radar'
    | 'resume'
    | 'content'
    | 'profile'
    | 'wallet'
    | 'dashboard'
    | 'system'
    | null;
  target: string | null;
  payload: Record<string, unknown> | null;
  related_resource_type: string | null;
  related_resource_id: string | null;
  tokens_charged: number;
  language: 'ar' | 'en' | null;
  created_at: string;
};

/** dashboard.markVisited — streak + "what changed since your last visit". */
export type VisitResultShape = {
  streakDays: number;
  previousVisit: string | null;
  firstVisit: boolean;
  changes: ActivityLogEntryShape[];
};

export type DraftSummaryShape = {
  resume: Array<{
    id: string;
    target_role: string;
    display_name: string;
    ats_score: number | null;
    updated_at: string;
    cache_id: string | null;
  }>;
  content: Array<{
    id: string;
    content_type: 'post' | 'carousel' | 'repurpose_bundle';
    display_title: string;
    topic: string;
    updated_at: string;
  }>;
  radar: Array<{
    id: string;
    target_role: string;
    current_score: number;
    target_score: number;
    created_at: string;
  }>;
};

export type SufficesForShape = {
  walletTotal: number;
  breakdown: {
    radar: number;
    resume: number;
    post: number;
    carousel: number;
    repurpose: number;
  };
  recommendedBundle: {
    labelKey: 'fullJourney' | 'contentPush' | 'roleRefresh' | 'topUpFirst';
    items: Array<{ type: string; count: number; totalCost: number }>;
    totalCost: number;
    remainingAfter: number;
  };
};

// ─────────────────────────────────────────────
// Career Plan shapes — mirror server/_core/lib/plan-engine.ts (ADR-006) so the
// client bundle doesn't pull in the engine. States, never numbers.
// ─────────────────────────────────────────────

export type PlanStepShape = {
  id: string;
  title: string;
  reason: string;
  pillar: 'resume' | 'content' | 'profile' | 'linkedin';
  deeplink: string;
  effort: 'very_low' | 'low' | 'medium' | 'high';
  state: 'ready' | 'needs' | 'incomplete';
};

export type PlanChangeShape = {
  id: string;
  label: string;
  at: string;
  deeplink: string | null;
};

export type CareerPlanShape = {
  hasSignal: boolean;
  goalLabel: string | null;
  lastUpdated: string | null;
  steps: PlanStepShape[];
  recentChanges: PlanChangeShape[];
};
